import React, { useEffect, useMemo, useState } from "react";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from "firebase/auth";
import { doc, onSnapshot, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";
import {
  QUIZPOPZ_PROVIDERS,
  generateQuizPopzFlashcards,
  generateQuizPopzQuiz,
  getQuizPopzCredits,
} from "./quizpopzApi";
import "./QuizPopz.css";

const QUESTION_COUNT_OPTIONS = [5, 10, 15, 20, 30];
const FLASHCARD_COUNT_OPTIONS = [4, 6, 8, 10, 12, 16, 20, 25];
const AI_FLASHCARD_MAX = 25;
const DEFAULT_MANUAL_FLASHCARD_COUNT = 4;
const AI_MONTHLY_CREDITS = 5000;
const QUIZ_AI_CREDIT_COST = 100;
const FLASHCARD_AI_CREDIT_COST = 70;
const DIFFICULTY_OPTIONS = ["Beginner", "Intermediate", "Advanced", "Mixed"];
const ROLE_STORAGE_PREFIX = "quizpopz.role.";
const QUIZ_HISTORY_LIMIT = 12;
const FLASHCARD_HISTORY_LIMIT = 12;
const THEME_STORAGE_KEY = "quizpopz.appearance";
const DEFAULT_ROLE = "student";
const CLOSED_PROVIDER_IDS = new Set(["anthropic", "google"]);
const ROLE_ORDER = ["teacher", "student", "parent"];
const ROLE_CONFIG = {
  teacher: {
    label: "Teacher",
    eyebrow: "Classroom assessment lab",
    heroTitle: "Teacher mode turns QuizPopz into a printable assessment studio.",
    heroBody:
      "Build polished quizzes for lessons, warm-ups, homework, revision sheets, and answer keys without losing structure or clarity.",
    topicLabel: "Lesson topic",
    audienceLabel: "Class or learner group",
    notesLabel: "Teacher notes",
    notesPlaceholder: "Mention standards, misconceptions to test, time limit, and whether you want recall or application.",
    audiencePlaceholder: "Example: Grade 7 science class",
    panelTitle: "Teacher-mode boosters",
    panelBody:
      "Teacher mode emphasizes fair distractors, clean answer keys, printable layout, and question variety that works in a classroom.",
    promptNudge:
      "Optimize for classroom printing, balanced distractors, and coverage that feels fair to a whole class.",
    motionWords: ["Exit ticket", "Homework", "Unit check", "Bell-ringer"],
    quickStarts: [
      {
        label: "Exit Ticket",
        caption: "Short formative check at the end of class.",
        topic: "Photosynthesis review for grade 7",
        audience: "Grade 7 science class",
        difficulty: "Intermediate",
        questionCount: 5,
        styleNotes: "Keep it classroom-ready, balanced, and printable. Include one application question.",
      },
      {
        label: "Homework Quiz",
        caption: "Slightly longer revision set with explanations.",
        topic: "Fractions operations review",
        audience: "Grade 6 math students",
        difficulty: "Intermediate",
        questionCount: 10,
        styleNotes: "Blend recall and application. Keep explanations short and useful for homework feedback.",
      },
      {
        label: "Unit Review",
        caption: "Broader coverage before a test.",
        topic: "World War 2 causes and turning points",
        audience: "Grade 8 history class",
        difficulty: "Mixed",
        questionCount: 15,
        styleNotes: "Cover multiple subtopics with fair distractors and a clean answer key.",
      },
    ],
    presets: {
      topic: "Photosynthesis review for grade 7",
      questionCount: 10,
      difficulty: "Intermediate",
      audience: "Grade 7 science class",
      styleNotes: "Blend recall and light application. Keep the quiz printable and explanation quality high.",
    },
  },
  student: {
    label: "Student",
    eyebrow: "Study sprint generator",
    heroTitle: "Student mode makes QuizPopz feel like a high-energy practice arena.",
    heroBody:
      "Generate study sets you can revise, edit, copy into notes, and print before a test or challenge a friend with later.",
    topicLabel: "Study topic",
    audienceLabel: "Who is studying",
    notesLabel: "Study notes",
    notesPlaceholder: "Mention what you keep forgetting, what kind of questions you want, or whether you want tougher distractors.",
    audiencePlaceholder: "Example: Me before my biology quiz",
    panelTitle: "Student-mode boosters",
    panelBody:
      "Student mode pushes for clearer explanations, confidence-building pacing, and practice that helps with self-study rather than grading.",
    promptNudge:
      "Optimize for self-study, motivating explanations, and questions that help one learner improve quickly.",
    motionWords: ["Speed review", "Challenge round", "Confidence boost", "Practice mode"],
    quickStarts: [
      {
        label: "Speed Review",
        caption: "Fast practice before a quiz or test.",
        topic: "Solar system science for grade 6",
        audience: "A middle school student revising alone",
        difficulty: "Intermediate",
        questionCount: 5,
        styleNotes: "Keep it fast, motivating, and explanation-rich without being too easy.",
      },
      {
        label: "Challenge Me",
        caption: "More demanding distractors and application.",
        topic: "Algebra expressions and equations",
        audience: "A student preparing for a math test",
        difficulty: "Advanced",
        questionCount: 10,
        styleNotes: "Make distractors clever but fair. Include application questions and crisp explanations.",
      },
      {
        label: "Study Buddy",
        caption: "Friendly review you can share with a friend.",
        topic: "Photosynthesis and respiration",
        audience: "Two students revising together",
        difficulty: "Mixed",
        questionCount: 10,
        styleNotes: "Keep wording friendly and explanations very clear for peer revision.",
      },
    ],
    presets: {
      topic: "Solar system science for grade 6",
      questionCount: 10,
      difficulty: "Intermediate",
      audience: "A middle school student revising alone",
      styleNotes: "Make distractors clever but fair. Keep explanations short and high-value.",
    },
  },
  parent: {
    label: "Parent",
    eyebrow: "Family learning coach",
    heroTitle: "Parent mode helps you create calm, family-friendly practice sheets.",
    heroBody:
      "Build quizzes you can edit with your child, print for practice, and copy into homework plans or family study sessions.",
    topicLabel: "Family learning topic",
    audienceLabel: "Child or learner profile",
    notesLabel: "Parent notes",
    notesPlaceholder: "Mention the child's age, confidence level, and whether you want gentle support or a stronger challenge.",
    audiencePlaceholder: "Example: My 10-year-old who needs help with fractions",
    panelTitle: "Parent-mode boosters",
    panelBody:
      "Parent mode steers the prompt toward patient wording, confidence-building explanations, and less intimidating pacing.",
    promptNudge:
      "Optimize for a supportive family-learning tone, clear explanations, and gentle but accurate challenge.",
    motionWords: ["Family study", "Gentle pacing", "Confidence", "Printable help"],
    quickStarts: [
      {
        label: "Homework Help",
        caption: "A gentle printable practice set.",
        topic: "Fractions basics",
        audience: "A 10-year-old who needs extra practice",
        difficulty: "Beginner",
        questionCount: 5,
        styleNotes: "Use warm wording, reduce intimidation, and explain answers clearly for a parent helping at home.",
      },
      {
        label: "Weekend Revision",
        caption: "A slightly longer review sheet for home.",
        topic: "States of matter",
        audience: "An elementary student revising with a parent",
        difficulty: "Intermediate",
        questionCount: 10,
        styleNotes: "Keep the tone patient and family-friendly with examples children understand.",
      },
      {
        label: "Confidence Builder",
        caption: "A confidence-first quiz with clear explanations.",
        topic: "Multiplication facts and patterns",
        audience: "A child who gets nervous during practice",
        difficulty: "Beginner",
        questionCount: 5,
        styleNotes: "Focus on confidence-building explanations and avoid trick wording.",
      },
    ],
    presets: {
      topic: "Fractions basics",
      questionCount: 5,
      difficulty: "Beginner",
      audience: "A 10-year-old who needs extra practice",
      styleNotes: "Keep the tone supportive, calm, and easy to print for home practice.",
    },
  },
};

function roleStorageKey(uid) {
  return `${ROLE_STORAGE_PREFIX}${uid}`;
}

function readStoredRole(uid) {
  if (!uid || typeof window === "undefined") return "";
  try {
    const value = window.localStorage.getItem(roleStorageKey(uid)) || "";
    return ROLE_CONFIG[value] ? value : "";
  } catch (_) {
    return "";
  }
}

function writeStoredRole(uid, role) {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(roleStorageKey(uid), role);
  } catch (_) {}
}

function clearStoredRole(uid) {
  if (!uid || typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(roleStorageKey(uid));
  } catch (_) {}
}

function quizpopzQuizzesDoc(uid) {
  return doc(db, "quizpopzQuizzes", uid);
}

function normalizeQuizHistoryList(value) {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeQuizHistoryEntry).filter(Boolean).slice(0, QUIZ_HISTORY_LIMIT);
}

function writeQuizHistory(uid, history) {
  if (!uid || !Array.isArray(history)) return;
  const entries = history.slice(0, QUIZ_HISTORY_LIMIT);
  setDoc(quizpopzQuizzesDoc(uid), { entries, updatedAt: serverTimestamp() }).catch(() => {});
}

function createQuizHistoryEntry(quiz, meta) {
  const safeQuiz = cloneQuizDraft(quiz);
  const createdAt = Date.now();
  const title = String(safeQuiz?.title || "Untitled quiz").trim() || "Untitled quiz";
  return {
    id: `${createdAt}-${Math.random().toString(36).slice(2, 8)}`,
    title,
    createdAt,
    meta: meta || null,
    quiz: safeQuiz,
    answers: {},
    activeQuestionIndex: 0,
    quizComplete: false,
  };
}

function sanitizeQuizAnswers(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value).reduce((answers, [key, choiceId]) => {
    const safeKey = String(key || "").slice(0, 140);
    const safeChoiceId = String(choiceId || "").slice(0, 12);
    if (safeKey && safeChoiceId) answers[safeKey] = safeChoiceId;
    return answers;
  }, {});
}

function normalizeQuizHistoryEntry(entry) {
  if (!entry || !entry.quiz || !entry.title) return null;
  const safeQuiz = cloneQuizDraft(entry.quiz);
  const questionTotal = Array.isArray(safeQuiz?.questions) ? safeQuiz.questions.length : 0;
  const activeQuestionIndex = Math.min(
    Math.max(0, Number(entry.activeQuestionIndex || 0) || 0),
    Math.max(0, questionTotal - 1)
  );
  return {
    id: String(entry.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    title: String(entry.title || safeQuiz?.title || "Untitled quiz").trim() || "Untitled quiz",
    createdAt: Number(entry.createdAt || Date.now()) || Date.now(),
    updatedAt: Number(entry.updatedAt || entry.createdAt || Date.now()) || Date.now(),
    completedAt: Number(entry.completedAt || 0) || 0,
    meta: entry.meta || null,
    quiz: safeQuiz,
    answers: sanitizeQuizAnswers(entry.answers),
    activeQuestionIndex,
    quizComplete: Boolean(entry.quizComplete),
  };
}

function quizpopzFlashcardsDoc(uid) {
  return doc(db, "quizpopzFlashcards", uid);
}

function normalizeFlashcardHistoryList(value) {
  if (!Array.isArray(value)) return [];
  return value.map(normalizeFlashcardHistoryEntry).filter(Boolean).slice(0, FLASHCARD_HISTORY_LIMIT);
}

function writeFlashcardHistory(uid, history) {
  if (!uid || !Array.isArray(history)) return;
  const entries = history.slice(0, FLASHCARD_HISTORY_LIMIT);
  setDoc(quizpopzFlashcardsDoc(uid), { entries, updatedAt: serverTimestamp() }).catch(() => {});
}

function createFlashcardHistoryEntry(deck, meta) {
  const safeDeck = cloneFlashcardDeck(deck);
  const createdAt = Date.now();
  const title = String(safeDeck?.title || "Untitled deck").trim() || "Untitled deck";
  return {
    id: `${createdAt}-${Math.random().toString(36).slice(2, 8)}`,
    title,
    createdAt,
    updatedAt: createdAt,
    meta: meta || null,
    deck: safeDeck,
  };
}

function normalizeFlashcardHistoryEntry(entry) {
  if (!entry || !entry.deck || !entry.title) return null;
  const safeDeck = cloneFlashcardDeck(entry.deck);
  if (!safeDeck || !Array.isArray(safeDeck.cards) || !safeDeck.cards.length) return null;
  return {
    id: String(entry.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    title: String(entry.title || safeDeck?.title || "Untitled deck").trim() || "Untitled deck",
    createdAt: Number(entry.createdAt || Date.now()) || Date.now(),
    updatedAt: Number(entry.updatedAt || entry.createdAt || Date.now()) || Date.now(),
    meta: entry.meta || null,
    deck: safeDeck,
  };
}

function readStoredTheme() {
  if (typeof window === "undefined") return "light";
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return value === "dark" ? "dark" : "light";
  } catch (_) {
    return "light";
  }
}

function writeStoredTheme(theme) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme === "dark" ? "dark" : "light");
  } catch (_) {}
}

function viewerInitials(viewer, fallbackLabel = "") {
  const source =
    (viewer && (viewer.displayName || (viewer.email ? viewer.email.split("@")[0] : ""))) ||
    fallbackLabel ||
    "";
  const cleaned = String(source).replace(/[_.-]+/g, " ").trim();
  if (!cleaned) return "QP";
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function currentCreditCycleKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
}

function formatCreditCycleLabel(monthKey) {
  if (!monthKey || !/^\d{4}-\d{2}$/.test(monthKey)) return "this month";
  const [year, month] = monthKey.split("-").map(Number);
  const cycleDate = new Date(year, month - 1, 1);
  return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(cycleDate);
}

function formatCreditResetDate(monthKey) {
  const safeMonthKey = monthKey && /^\d{4}-\d{2}$/.test(monthKey) ? monthKey : currentCreditCycleKey();
  const [year, month] = safeMonthKey.split("-").map(Number);
  const resetDate = new Date(year, month, 1);
  const resetMonth = String(resetDate.getMonth() + 1).padStart(2, "0");
  const resetDay = String(resetDate.getDate()).padStart(2, "0");
  return `${resetMonth}/${resetDay}/${resetDate.getFullYear()}`;
}

function formatCreditCount(value) {
  return Math.max(0, Number(value || 0)).toLocaleString();
}

function aiCreditsRemaining(aiCredits) {
  return Math.max(0, Math.floor(Number(aiCredits?.remainingCredits ?? AI_MONTHLY_CREDITS)));
}

function hasAiCreditsFor(aiCredits, cost) {
  return aiCreditsRemaining(aiCredits) >= cost;
}

function formatElapsedTime(totalSeconds) {
  const safeSeconds = Math.max(0, Number(totalSeconds || 0));
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function createDefaultAiCredits(monthKey = currentCreditCycleKey()) {
  return {
    cycleMonth: monthKey,
    monthlyAllocation: AI_MONTHLY_CREDITS,
    remainingCredits: AI_MONTHLY_CREDITS,
  };
}

function normalizeAiCreditsRecord(data, monthKey = currentCreditCycleKey()) {
  if (!data || data.cycleMonth !== monthKey) {
    return createDefaultAiCredits(monthKey);
  }

  const nextRemaining = Math.min(
    AI_MONTHLY_CREDITS,
    Math.max(0, Number.isFinite(data.remainingCredits) ? Math.floor(data.remainingCredits) : AI_MONTHLY_CREDITS)
  );

  return {
    cycleMonth: monthKey,
    monthlyAllocation: AI_MONTHLY_CREDITS,
    remainingCredits: nextRemaining,
  };
}

function quizpopzCreditsDoc(uid) {
  return doc(db, "quizpopzCredits", uid);
}

function cloneQuizDraft(value) {
  if (!value) return null;
  return JSON.parse(JSON.stringify(value));
}

function cloneFlashcardDeck(value) {
  if (!value) return null;
  return JSON.parse(JSON.stringify(value));
}

function createEmptyFlashcard(index = 0) {
  const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return {
    id: `card-${index + 1}-${uniqueSuffix}`,
    front: "",
    back: "",
    hint: "",
  };
}

function syncFlashcardsToCount(cards = [], count = 6) {
  return Array.from({ length: count }, (_, index) => {
    const existing = cards[index] || {};
    return {
      id: existing.id || createEmptyFlashcard(index).id,
      front: existing.front || "",
      back: existing.back || "",
      hint: existing.hint || "",
    };
  });
}

function clampAiFlashcardCount(value) {
  return Math.min(Math.max(Number(value || 8) || 8, 4), AI_FLASHCARD_MAX);
}

function roleLabelFor(role) {
  return ROLE_CONFIG[role]?.label || "Student";
}

function quizQuestionKey(question, index) {
  return question && question.id ? question.id : `question-${index}`;
}

function scoreQuizDraft(quiz, answers) {
  const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];
  const correct = questions.reduce((total, question, index) => {
    const selectedChoiceId = answers[quizQuestionKey(question, index)];
    return selectedChoiceId && selectedChoiceId === question.correctChoiceId ? total + 1 : total;
  }, 0);
  const answered = questions.reduce((total, question, index) => (
    answers[quizQuestionKey(question, index)] ? total + 1 : total
  ), 0);
  return {
    total: questions.length,
    answered,
    correct,
    notCorrect: Math.max(0, questions.length - correct),
  };
}

function buildManualFlashcardDeck({ topic, audience, role, cards }) {
  const cleanTopic = String(topic || "Flashcard Deck").trim() || "Flashcard Deck";
  const cleanAudience = String(audience || "General learners").trim() || "General learners";
  const cleanCards = (cards || [])
    .map((card, index) => ({
      id: card.id || `card${index + 1}`,
      front: String(card.front || "").trim(),
      back: String(card.back || "").trim(),
      hint: String(card.hint || "").trim(),
    }))
    .filter((card) => card.front || card.back || card.hint);

  return {
    title: `${cleanTopic} Flashcards`,
    description: `A ${cleanCards.length}-card ${roleLabelFor(role).toLowerCase()} deck for ${cleanAudience}.`,
    studyTip: "Flip each card once for recognition, then again only after trying to say the answer out loud.",
    cards: cleanCards,
  };
}

function formatQuizText(quiz) {
  if (!quiz) return "";
  const lines = [quiz.title || "Quiz", "", quiz.description || "", "", `Instructions: ${quiz.instructions || ""}`, ""];
  quiz.questions.forEach((question, index) => {
    lines.push(`${index + 1}. ${question.prompt}`);
    question.choices.forEach((choice) => {
      lines.push(`   ${choice.id}. ${choice.text}`);
    });
    lines.push("");
  });
  return lines.join("\n").trim();
}

function formatAnswerKeyText(quiz) {
  if (!quiz) return "";
  const lines = [`${quiz.title || "Quiz"} - Answer Key`, ""];
  quiz.questions.forEach((question, index) => {
    lines.push(`${index + 1}. ${question.correctChoiceId} - ${question.explanation}`);
  });
  return lines.join("\n").trim();
}

function normalizeFlashcardText(value) {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  return "";
}

function flashcardTextOrFallback(value, fallback) {
  const normalized = normalizeFlashcardText(value);
  return normalized || fallback;
}

function formatFlashcardDeckText(deck) {
  if (!deck) return "";
  const lines = [
    flashcardTextOrFallback(deck.title, "Flashcard Deck"),
    "",
    normalizeFlashcardText(deck.description),
    "",
    `Study tip: ${flashcardTextOrFallback(deck.studyTip, "Flip through the deck twice: once for recognition, then again for recall.")}`,
    "",
  ];
  deck.cards.forEach((card, index) => {
    const front = flashcardTextOrFallback(card && card.front, `Card ${index + 1}`);
    const back = flashcardTextOrFallback(card && card.back, "Add the back of this flashcard.");
    const hint = normalizeFlashcardText(card && card.hint);
    lines.push(`${index + 1}. FRONT: ${front}`);
    lines.push(`   BACK: ${back}`);
    if (hint) lines.push(`   HINT: ${hint}`);
    lines.push("");
  });
  return lines.join("\n").trim();
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

async function copyText(text) {
  const normalizedText = String(text || "");
  if (!normalizedText.trim()) {
    throw new Error("Nothing to copy.");
  }

  if (typeof navigator !== "undefined" && navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(normalizedText);
    return;
  }
  if (typeof document === "undefined") {
    throw new Error("Clipboard unavailable.");
  }
  const textarea = document.createElement("textarea");
  textarea.value = normalizedText;
  textarea.setAttribute("readonly", "readonly");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error("Clipboard unavailable.");
  }
}

function printHtmlDocument(htmlString) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return false;
  }

  const frame = document.createElement("iframe");
  frame.setAttribute("aria-hidden", "true");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  frame.style.opacity = "0";
  frame.style.pointerEvents = "none";
  document.body.appendChild(frame);

  const frameDoc = frame.contentDocument || (frame.contentWindow && frame.contentWindow.document);
  if (!frameDoc) {
    if (frame.parentNode) frame.parentNode.removeChild(frame);
    return false;
  }

  let didPrint = false;

  const cleanup = () => {
    window.setTimeout(() => {
      if (frame.parentNode) {
        frame.parentNode.removeChild(frame);
      }
    }, 0);
  };

  const triggerPrint = () => {
    if (didPrint) return;
    didPrint = true;

    const frameWindow = frame.contentWindow;
    if (!frameWindow) {
      cleanup();
      return;
    }

    try {
      frameWindow.focus();
      frameWindow.print();
      frameWindow.addEventListener("afterprint", cleanup, { once: true });
      window.setTimeout(cleanup, 1600);
    } catch (_) {
      cleanup();
    }
  };

  frame.addEventListener("load", () => {
    window.setTimeout(triggerPrint, 0);
  }, { once: true });

  frameDoc.open();
  frameDoc.write(htmlString);
  frameDoc.close();

  // Fallback for browsers that do not reliably fire iframe load for about:blank writes.
  window.setTimeout(triggerPrint, 450);
  return true;
}

function buildQuizPrintDocument(quiz) {
  const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];
  const questionMarkup = questions.map((question, index) => {
    const choicesMarkup = (question.choices || []).map((choice) => `
      <li><strong>${escapeHtml(choice.id)}.</strong> ${escapeHtml(choice.text)}</li>
    `).join("");
    return `
      <article class="print-question">
        <span>Question ${index + 1}</span>
        <h2>${escapeHtml(question.prompt)}</h2>
        <ol type="A">${choicesMarkup}</ol>
      </article>
    `;
  }).join("");

  const answerMarkup = questions.map((question, index) => `
    <li>
      <strong>${index + 1}. ${escapeHtml(question.correctChoiceId)}</strong>
      <p>${escapeHtml(question.explanation)}</p>
    </li>
  `).join("");

  return `<!doctype html>
    <html>
      <head>
        <title>${escapeHtml(quiz?.title || "QuizPopz Quiz")}</title>
        <style>
          body { font-family: "Trebuchet MS", Arial, sans-serif; margin: 32px; color: #16213e; }
          h1 { margin: 0 0 8px; font-size: 32px; }
          h2 { margin: 8px 0 14px; font-size: 20px; }
          p { line-height: 1.6; }
          .print-meta { margin-bottom: 24px; }
          .print-question, .answer-key { border: 1px solid #d7dfe7; border-radius: 18px; padding: 18px; margin-bottom: 16px; break-inside: avoid; }
          .print-question span, .answer-key span { display: inline-block; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.12em; font-size: 12px; color: #58708d; }
          .print-question ol, .answer-key ol { margin: 0; padding-left: 22px; }
          .print-question li, .answer-key li { margin: 8px 0; line-height: 1.5; }
          .answer-key { margin-top: 28px; }
          .answer-key p { margin: 6px 0 0; }
        </style>
      </head>
      <body>
        <section class="print-meta">
          <h1>${escapeHtml(quiz?.title || "QuizPopz Quiz")}</h1>
          <p>${escapeHtml(quiz?.description || "")}</p>
          <p><strong>Instructions:</strong> ${escapeHtml(quiz?.instructions || "")}</p>
        </section>
        <section>${questionMarkup}</section>
        <section class="answer-key">
          <span>Answer key</span>
          <ol>${answerMarkup}</ol>
        </section>
      </body>
    </html>`;
}

function prettyAuthError(err) {
  const code = err && err.code;
  switch (code) {
    case "auth/invalid-email":
      return "That email address does not look right.";
    case "auth/missing-password":
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/email-already-in-use":
      return "That email is already registered. Try signing in instead.";
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Wrong email or password.";
    case "auth/too-many-requests":
      return "Too many tries. Wait a minute and try again.";
    case "auth/operation-not-allowed":
      return "This sign-in method is not enabled in Firebase yet.";
    case "auth/network-request-failed":
      return "Network problem. Check your connection and try again.";
    default:
      return (err && err.message) || "Could not complete authentication.";
  }
}

function QuizQuestionCard({
  question,
  index,
  editable,
  onQuestionFieldChange,
  onChoiceChange,
  onCorrectChoiceChange,
}) {
  return (
    <article
      className={`quizpopz-question-card ${editable ? "is-editing" : ""}`}
      style={{ "--quizpopz-stagger": `${index * 80}ms` }}
    >
      <div className="quizpopz-question-topline">
        <span className="quizpopz-question-number">Question {index + 1}</span>
        <span className="quizpopz-question-difficulty">{question.difficulty || "Mixed"}</span>
      </div>
      {editable ? (
        <div className="quizpopz-editor-stack">
          <label className="quizpopz-inline-field">
            <span>Question prompt</span>
            <textarea
              rows="3"
              value={question.prompt}
              onChange={(event) => onQuestionFieldChange(index, "prompt", event.target.value)}
            />
          </label>

          <div className="quizpopz-editor-grid">
            <label className="quizpopz-inline-field">
              <span>Difficulty</span>
              <select
                value={question.difficulty}
                onChange={(event) => onQuestionFieldChange(index, "difficulty", event.target.value)}
              >
                {DIFFICULTY_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
            <div className="quizpopz-answer-box">
              <strong>Correct answer:</strong> {question.correctChoiceId}
            </div>
          </div>

          <div className="quizpopz-choice-editor-list">
            {question.choices.map((choice, choiceIndex) => (
              <div
                key={choice.id}
                className={`quizpopz-choice-editor ${choice.id === question.correctChoiceId ? "is-correct" : ""}`}
              >
                <div className="quizpopz-choice-editor-head">
                  <span className="quizpopz-choice-badge">{choice.id}</span>
                  <button
                    type="button"
                    className="quizpopz-mini-btn"
                    onClick={() => onCorrectChoiceChange(index, choice.id)}
                  >
                    Mark correct
                  </button>
                </div>
                <input
                  type="text"
                  value={choice.text}
                  onChange={(event) => onChoiceChange(index, choiceIndex, event.target.value)}
                />
              </div>
            ))}
          </div>

          <label className="quizpopz-inline-field">
            <span>Explanation</span>
            <textarea
              rows="3"
              value={question.explanation}
              onChange={(event) => onQuestionFieldChange(index, "explanation", event.target.value)}
            />
          </label>
        </div>
      ) : (
        <>
          <h3>{question.prompt}</h3>
          <ul className="quizpopz-choice-list">
            {question.choices.map((choice) => (
              <li
                key={choice.id}
                className={choice.id === question.correctChoiceId ? "is-correct" : ""}
              >
                <span className="quizpopz-choice-badge">{choice.id}</span>
                <span>{choice.text}</span>
              </li>
            ))}
          </ul>
          <div className="quizpopz-answer-box">
            <strong>Correct answer:</strong> {question.correctChoiceId}
          </div>
          <p className="quizpopz-explanation">{question.explanation}</p>
        </>
      )}
    </article>
  );
}

function QuizPlayer({
  quiz,
  activeIndex,
  answers,
  isComplete,
  score,
  onAnswer,
  onPrevious,
  onNext,
  onFinish,
  onRetake,
  onJump,
}) {
  const questions = Array.isArray(quiz?.questions) ? quiz.questions : [];
  if (!questions.length) return null;

  const boundedIndex = Math.min(Math.max(activeIndex, 0), questions.length - 1);
  const question = questions[boundedIndex];
  const questionKey = quizQuestionKey(question, boundedIndex);
  const selectedChoiceId = answers[questionKey] || "";
  const isLastQuestion = boundedIndex === questions.length - 1;
  const progressPercent = questions.length > 0 ? ((boundedIndex + 1) / questions.length) * 100 : 0;
  const selectedChoice = (question.choices || []).find((choice) => choice.id === selectedChoiceId);
  const correctChoice = (question.choices || []).find((choice) => choice.id === question.correctChoiceId);
  const activeIsCorrect = selectedChoiceId && selectedChoiceId === question.correctChoiceId;

  return (
    <div className="quizpopz-quiz-player">
      {isComplete && (
        <div className="quizpopz-score-card">
          <span className="quizpopz-summary-kicker">Final score</span>
          <h3>{score.correct} correct out of {score.total}</h3>
          <div className="quizpopz-score-pill-row">
            <div className="quizpopz-score-pill is-correct">
              <span aria-hidden="true">✓</span>
              <strong>{score.correct}</strong>
              <em>Correct</em>
            </div>
            <div className="quizpopz-score-pill is-incorrect">
              <span aria-hidden="true">x</span>
              <strong>{score.notCorrect}</strong>
              <em>Incorrect</em>
            </div>
          </div>
          <p className="quizpopz-score-subline">{score.answered} answered out of {score.total}.</p>
          <button type="button" className="quizpopz-action-btn" onClick={onRetake}>
            Take again
          </button>
        </div>
      )}

      <article className={`quizpopz-question-card quizpopz-player-card ${isComplete ? "is-reviewed" : ""}`}>
        <div className="quizpopz-question-topline">
          <span className="quizpopz-question-number">Question {boundedIndex + 1} of {questions.length}</span>
          <span className="quizpopz-question-difficulty">{question.difficulty || "Mixed"}</span>
        </div>
        <div className="quizpopz-player-progress" aria-hidden="true">
          <span style={{ width: `${progressPercent}%` }} />
        </div>
        <h3>{question.prompt}</h3>
        <div className="quizpopz-choice-button-list">
          {(question.choices || []).map((choice) => {
            const isSelected = choice.id === selectedChoiceId;
            const isCorrect = choice.id === question.correctChoiceId;
            const isWrongSelection = isComplete && isSelected && !isCorrect;
            return (
              <button
                key={choice.id}
                type="button"
                className={[
                  "quizpopz-choice-option",
                  isSelected ? "is-selected" : "",
                  isComplete && isCorrect ? "is-correct" : "",
                  isWrongSelection ? "is-incorrect" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => onAnswer(boundedIndex, choice.id)}
                disabled={isComplete}
                aria-pressed={isSelected}
              >
                <span className="quizpopz-choice-badge">{choice.id}</span>
                <span>{choice.text}</span>
              </button>
            );
          })}
        </div>

        {isComplete && (
          <>
            <div className={`quizpopz-answer-box ${activeIsCorrect ? "is-correct" : "is-incorrect"}`}>
              <strong>{activeIsCorrect ? "Correct" : "Incorrect"}:</strong>{" "}
              {selectedChoice ? `You chose ${selectedChoice.id}.` : "No answer selected."} Correct answer: {correctChoice ? `${correctChoice.id}. ${correctChoice.text}` : question.correctChoiceId}.
            </div>
            <p className="quizpopz-explanation">{question.explanation}</p>
          </>
        )}

        <div className="quizpopz-player-controls">
          <button type="button" className="quizpopz-action-btn quizpopz-nav-btn" onClick={onPrevious} disabled={boundedIndex === 0}>
            Previous
          </button>
          <div className="quizpopz-flashcard-progress">
            {score.answered} of {score.total} answered
          </div>
          {!isComplete && isLastQuestion ? (
            <button type="button" className="quizpopz-action-btn quizpopz-nav-btn quizpopz-nav-btn--next" onClick={onFinish}>
              Finish quiz
            </button>
          ) : (
            <button type="button" className="quizpopz-action-btn quizpopz-nav-btn quizpopz-nav-btn--next" onClick={onNext} disabled={boundedIndex === questions.length - 1}>
              Next
            </button>
          )}
        </div>

        <div className="quizpopz-question-jump-list" aria-label="Question navigation">
          {questions.map((entry, index) => {
            const key = quizQuestionKey(entry, index);
            const selected = answers[key] || "";
            const answered = Boolean(selected);
            const correct = answered && selected === entry.correctChoiceId;
            return (
              <button
                key={key}
                type="button"
                className={[
                  "quizpopz-question-jump",
                  index === boundedIndex ? "is-active" : "",
                  answered ? "is-answered" : "",
                  isComplete && correct ? "is-correct" : "",
                  isComplete && answered && !correct ? "is-incorrect" : "",
                  isComplete && !answered ? "is-unanswered" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => onJump(index)}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
      </article>

      {isComplete && (
        <div className="quizpopz-review-card">
          <span className="quizpopz-summary-kicker">Answer review</span>
          <ol className="quizpopz-review-list">
            {questions.map((entry, index) => {
              const key = quizQuestionKey(entry, index);
              const selected = answers[key] || "";
              const correct = selected && selected === entry.correctChoiceId;
              const selectedEntry = (entry.choices || []).find((choice) => choice.id === selected);
              const correctEntry = (entry.choices || []).find((choice) => choice.id === entry.correctChoiceId);
              return (
                <li key={`${key}-review`} className={correct ? "is-correct" : "is-incorrect"}>
                  <strong>{index + 1}. {correct ? "Correct" : "Incorrect"}</strong>
                  <span>{entry.prompt}</span>
                  <p><b>Your answer:</b> {selectedEntry ? `${selectedEntry.id}. ${selectedEntry.text}` : "Not answered"}</p>
                  <p><b>Correct answer:</b> {correctEntry ? `${correctEntry.id}. ${correctEntry.text}` : entry.correctChoiceId}</p>
                </li>
              );
            })}
          </ol>
        </div>
      )}
    </div>
  );
}

function FlashcardEditorCard({ card, index, onFieldChange, onRemove, canRemove = true, tone = "Deck Card" }) {
  return (
    <article className="quizpopz-flashcard-editor-card" style={{ "--quizpopz-stagger": `${index * 70}ms` }}>
      <div className="quizpopz-question-topline">
        <span className="quizpopz-question-number">Card {index + 1}</span>
        <div className="quizpopz-manual-card-actions">
          <span className="quizpopz-question-difficulty">{tone}</span>
          {onRemove && (
            <button type="button" className="quizpopz-mini-btn" onClick={() => onRemove(index)} disabled={!canRemove}>
              Remove
            </button>
          )}
        </div>
      </div>
      <label className="quizpopz-inline-field">
        <span>Front</span>
        <textarea rows="2" value={card.front} onChange={(event) => onFieldChange(index, "front", event.target.value)} />
      </label>
      <label className="quizpopz-inline-field">
        <span>Back</span>
        <textarea rows="3" value={card.back} onChange={(event) => onFieldChange(index, "back", event.target.value)} />
      </label>
      <label className="quizpopz-inline-field">
        <span>Hint</span>
        <input type="text" value={card.hint} onChange={(event) => onFieldChange(index, "hint", event.target.value)} />
      </label>
    </article>
  );
}

function FlashcardStage({ deck, activeIndex, flippedCards, onFlip, onPrev, onNext, onJump }) {
  if (!deck || !Array.isArray(deck.cards) || deck.cards.length === 0) return null;
  const activeCard = deck.cards[activeIndex] || deck.cards[0];
  const isFlipped = !!flippedCards[activeCard.id];
  const activeFront = flashcardTextOrFallback(activeCard.front, "Add a front to this flashcard.");
  const activeBack = flashcardTextOrFallback(activeCard.back, "Add the back of this flashcard.");
  const activeHint = normalizeFlashcardText(activeCard.hint);
  const frontHint = activeHint || "Tap to flip and reveal the back.";
  const backHint = activeHint || "Use the hint to make recall easier on the next pass.";

  return (
    <div className="quizpopz-flashcard-stage-shell">
      <div className="quizpopz-flashcard-stack">
        <div className="quizpopz-flashcard-ghost quizpopz-flashcard-ghost-back" />
        <div className="quizpopz-flashcard-ghost quizpopz-flashcard-ghost-mid" />
        <button
          type="button"
          className={`quizpopz-flashcard ${isFlipped ? "is-flipped" : ""}`}
          onClick={() => onFlip(activeIndex)}
        >
          <span className="quizpopz-flashcard-inner">
            <span className="quizpopz-flashcard-face quizpopz-flashcard-face-front">
              <span className="quizpopz-flashcard-kicker">Front of card</span>
              <h3>{activeFront}</h3>
              <p>{frontHint}</p>
            </span>
            <span className="quizpopz-flashcard-face quizpopz-flashcard-face-back">
              <span className="quizpopz-flashcard-kicker">Back of card</span>
              <h3>{activeBack}</h3>
              <p>{backHint}</p>
            </span>
          </span>
        </button>
      </div>

      <div className="quizpopz-flashcard-controls">
        <button type="button" className="quizpopz-action-btn quizpopz-nav-btn" onClick={onPrev}>
          ← Prev Card
        </button>
        <div className="quizpopz-flashcard-progress">
          Card {activeIndex + 1} of {deck.cards.length}
        </div>
        <button type="button" className="quizpopz-action-btn quizpopz-nav-btn quizpopz-nav-btn--next" onClick={onNext}>
          Next Card →
        </button>
      </div>

      <div className="quizpopz-flashcard-rail">
        {deck.cards.map((card, index) => {
          const label = flashcardTextOrFallback(card && card.front, `Card ${index + 1}`);
          return (
            <button
              key={card.id || `${card.front}-${index}`}
              type="button"
              className={`quizpopz-flashcard-rail-item ${index === activeIndex ? "is-active" : ""}`}
              onClick={() => onJump(index)}
            >
              <strong>{index + 1}</strong>
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function QuizPopzAiCreditsPanel({ aiCredits, creditsBusy, creditsError, compact = false, requiredCost = Math.min(QUIZ_AI_CREDIT_COST, FLASHCARD_AI_CREDIT_COST) }) {
  const allocation = aiCredits?.monthlyAllocation || AI_MONTHLY_CREDITS;
  const remaining = aiCreditsRemaining(aiCredits);
  const used = Math.max(0, allocation - remaining);
  const fillPercent = allocation > 0 ? Math.max(0, Math.min(100, (remaining / allocation) * 100)) : 0;
  const activeCost = Math.max(0, Number(requiredCost || 0));
  const isBlocked = remaining < activeCost;
  const resetDate = formatCreditResetDate(aiCredits?.cycleMonth);
  const panelClassName = `${compact ? "quizpopz-ai-credit-bar" : "quizpopz-status-panel"} quizpopz-credit-panel ${isBlocked ? "is-empty" : ""}`;

  return (
    <div className={panelClassName} role="status" aria-live="polite">
      <div className="quizpopz-credit-head">
        <span>AI credits left</span>
        <strong>{creditsBusy ? "Syncing..." : `${formatCreditCount(remaining)} / ${formatCreditCount(allocation)}`}</strong>
      </div>
      <div className="quizpopz-credit-meter" aria-hidden="true">
        <span style={{ width: `${fillPercent}%` }} />
      </div>
      <small className={`quizpopz-credit-reset ${isBlocked ? "is-warning" : ""}`}>
        {creditsBusy
          ? "Syncing monthly credits."
          : isBlocked
            ? `Wait till 1 month: ${resetDate}`
            : `Resets ${resetDate}. Used ${formatCreditCount(used)} this month.`}
      </small>
      {!compact && (
        <div className="quizpopz-credit-breakdown">
          <span>Quiz: {QUIZ_AI_CREDIT_COST} credits</span>
          <span>AI flashcards: {FLASHCARD_AI_CREDIT_COST} credits</span>
          <span>Cycle: {formatCreditCycleLabel(aiCredits?.cycleMonth)}</span>
        </div>
      )}
      {creditsError && <p className="quizpopz-credit-error">{creditsError}</p>}
    </div>
  );
}

function QuizPopzBoot() {
  return (
    <main className="quizpopz-page quizpopz-role-guest">
      <section className="quizpopz-entry-shell quizpopz-entry-shell--boot">
        <div className="quizpopz-entry-backdrop">
          <span className="quizpopz-backdrop-orb quizpopz-backdrop-orb-a" />
          <span className="quizpopz-backdrop-orb quizpopz-backdrop-orb-b" />
          <span className="quizpopz-backdrop-orb quizpopz-backdrop-orb-c" />
        </div>
        <div className="quizpopz-loading-card">
          <div className="quizpopz-loader-rings">
            <span />
            <span />
            <span />
          </div>
          <span className="quizpopz-eyebrow">QuizPopz loading</span>
          <h1>Preparing the animated quiz studio</h1>
          <p>Checking authentication and building the right experience before anyone enters.</p>
        </div>
      </section>
    </main>
  );
}

function QuizPopzAuthModal({
  authMode,
  authName,
  authRole,
  authEmail,
  authPassword,
  authBusy,
  authError,
  onModeChange,
  onNameChange,
  onRoleChange,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  onClose,
}) {
  return (
    <div
      className="qp-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Log in or sign up"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="qp-modal">
        <button type="button" className="qp-modal-close" onClick={onClose} aria-label="Close login">
          ×
        </button>
        <div className="qp-modal-head">
          <span className="qp-modal-kicker">QuizPopz</span>
          <h2>Log in or Sign up</h2>
          <p>Save your popped quizzes, forged flashcards, and monthly AI credits.</p>
        </div>

        <div className="quizpopz-auth-toggle qp-modal-toggle">
          <button
            type="button"
            className={authMode === "login" ? "is-active" : ""}
            onClick={() => onModeChange("login")}
          >
            Log in
          </button>
          <button
            type="button"
            className={authMode === "signup" ? "is-active" : ""}
            onClick={() => onModeChange("signup")}
          >
            Sign up
          </button>
        </div>

        <form className="qp-modal-form" onSubmit={onSubmit}>
          {authMode === "signup" && (
            <label className="quizpopz-field">
              <span>Display name</span>
              <input
                type="text"
                value={authName}
                onChange={(event) => onNameChange(event.target.value)}
                placeholder="How should QuizPopz greet you?"
                autoComplete="name"
                minLength={2}
                maxLength={80}
                required
              />
            </label>
          )}

          {authMode === "signup" && (
            <div className="quizpopz-field qp-modal-role-field">
              <span>I am a...</span>
              <div className="qp-modal-role" role="group" aria-label="Choose your role">
                {ROLE_ORDER.map((roleId) => (
                  <button
                    key={roleId}
                    type="button"
                    className={`qp-modal-role-btn ${authRole === roleId ? "is-active" : ""}`}
                    onClick={() => onRoleChange(roleId)}
                  >
                    {ROLE_CONFIG[roleId].label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <label className="quizpopz-field">
            <span>Email</span>
            <input
              type="email"
              value={authEmail}
              onChange={(event) => onEmailChange(event.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
          </label>

          <label className="quizpopz-field">
            <span>Password</span>
            <input
              type="password"
              value={authPassword}
              onChange={(event) => onPasswordChange(event.target.value)}
              placeholder="At least 6 characters"
              autoComplete={authMode === "login" ? "current-password" : "new-password"}
              required
              minLength={6}
            />
          </label>

          {authError && <div className="quizpopz-error-banner">{authError}</div>}

          <button type="submit" className="quizpopz-generate-btn" disabled={authBusy}>
            {authBusy
              ? authMode === "login"
                ? "Logging in..."
                : "Creating account..."
              : authMode === "login"
                ? "Log in"
                : "Create account"}
          </button>
        </form>
      </div>
    </div>
  );
}

function QuizPopzSidebar({
  open,
  onToggle,
  theme,
  onSetTheme,
  viewer,
  viewerLabel,
  viewerEmail,
  viewerInitialsLabel,
  creditsRemaining,
  creditsBusy,
  activeGenerator,
  onNewQuiz,
  onNewFlashcardDeck,
  onHistory,
  onSearch,
  settingsOpen,
  onToggleSettings,
  onSignOut,
  onLogin,
  searchValue,
  onSearchChange,
  quizList,
  flashcardList,
  activeQuizHistoryId,
  activeFlashcardHistoryId,
  onSelectQuiz,
  onSelectFlashcard,
  quizListOpen,
  onToggleQuizList,
  flashcardListOpen,
  onToggleFlashcardList,
}) {
  return (
    <aside className={`qp-sidebar ${open ? "is-open" : "is-collapsed"}`}>
      <div className="qp-sidebar-top">
        <button
          type="button"
          className="qp-logo-toggle"
          onClick={onToggle}
          aria-label={open ? "Collapse sidebar" : "Open sidebar"}
        >
          <span className="qp-logo-mark" aria-hidden="true">
            <img src="/vmcreation-logo.svg" alt="" className="qp-logo-img" />
            <svg className="qp-logo-panel-icon" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
              <rect x="3" y="4" width="18" height="16" rx="2.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
              <line x1="9" y1="4" x2="9" y2="20" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          </span>
          <span className="qp-logo-hover-label">open sidebar</span>
        </button>
        {open && (
          <a
            className="qp-logo-word"
            href="https://vmcreation.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            vmcreation
          </a>
        )}
      </div>

      <nav className="qp-sidebar-nav" aria-label="QuizPopz actions">
        <button
          type="button"
          className={`qp-nav-item ${activeGenerator === "flashcards" ? "is-active" : ""}`}
          onClick={onNewFlashcardDeck}
          data-tooltip="New Flashcard Deck"
        >
          <span className="qp-nav-emoji" aria-hidden="true">🃏</span>
          <span className="qp-nav-label">New Flashcard Deck</span>
        </button>
        <button
          type="button"
          className={`qp-nav-item ${activeGenerator === "quiz" ? "is-active" : ""}`}
          onClick={onNewQuiz}
          data-tooltip="New Quiz"
        >
          <span className="qp-nav-emoji" aria-hidden="true">📝</span>
          <span className="qp-nav-label">New Quiz</span>
        </button>
        <button
          type="button"
          className="qp-nav-item qp-nav-item--open-only"
          onClick={onHistory}
          data-tooltip="History"
        >
          <span className="qp-nav-emoji" aria-hidden="true">🕘</span>
          <span className="qp-nav-label">History</span>
        </button>
        <div className="qp-nav-settings-wrap qp-nav-item--open-only">
          <button
            type="button"
            className={`qp-nav-item ${settingsOpen ? "is-active" : ""}`}
            onClick={onToggleSettings}
            data-tooltip="Settings"
            aria-expanded={settingsOpen}
          >
            <span className="qp-nav-emoji" aria-hidden="true">⚙️</span>
            <span className="qp-nav-label">Settings</span>
          </button>
          {settingsOpen && (
            <div className="qp-settings-popover">
              <div className="qp-settings-section">
                <span className="qp-settings-title">Appearance</span>
                <div className="qp-theme-toggle">
                  <button
                    type="button"
                    className={theme === "light" ? "is-active" : ""}
                    onClick={() => onSetTheme("light")}
                  >
                    ☀️ Light
                  </button>
                  <button
                    type="button"
                    className={theme === "dark" ? "is-active" : ""}
                    onClick={() => onSetTheme("dark")}
                  >
                    🌙 Dark
                  </button>
                </div>
              </div>
              {viewer ? (
                <button type="button" className="qp-settings-logout" onClick={onSignOut}>
                  Log out
                </button>
              ) : (
                <button type="button" className="qp-settings-login" onClick={onLogin}>
                  Log in or sign up
                </button>
              )}
            </div>
          )}
        </div>
        <button
          type="button"
          className="qp-nav-item"
          onClick={onSearch}
          data-tooltip="Search"
        >
          <span className="qp-nav-emoji" aria-hidden="true">🔍</span>
          <span className="qp-nav-label">Search</span>
        </button>
      </nav>

      <div className="qp-sidebar-lists">
        <div className="qp-sidebar-search-row">
          <span className="qp-search-icon" aria-hidden="true">🔍</span>
          <input
            id="quizpopz-sidebar-search"
            type="text"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search titles..."
            aria-label="Search quizzes and flashcards"
          />
        </div>

        <div className="qp-list-group">
          <button
            type="button"
            className="qp-list-header"
            onClick={onToggleQuizList}
            aria-expanded={quizListOpen}
          >
            <span className={`qp-caret ${quizListOpen ? "is-open" : ""}`} aria-hidden="true">▸</span>
            <span className="qp-list-name">Popped Quizzes</span>
            <span className="qp-list-count">{quizList.length}</span>
          </button>
          {quizListOpen && (
            <ul className="qp-list">
              {quizList.length === 0 ? (
                <li className="qp-list-empty">{viewer ? "No quizzes yet." : "Log in to save quizzes."}</li>
              ) : (
                quizList.map((entry) => (
                  <li key={entry.id}>
                    <button
                      type="button"
                      className={`qp-list-item ${activeQuizHistoryId === entry.id ? "is-active" : ""}`}
                      onClick={() => onSelectQuiz(entry)}
                      title={entry.quizComplete ? `${entry.title} — your answers saved` : entry.title}
                    >
                      <span className="qp-list-item-emoji" aria-hidden="true">📝</span>
                      <span className="qp-list-item-title">{entry.title}</span>
                      {entry.quizComplete && <span className="qp-list-item-badge" aria-hidden="true">✓</span>}
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

        <div className="qp-list-group">
          <button
            type="button"
            className="qp-list-header"
            onClick={onToggleFlashcardList}
            aria-expanded={flashcardListOpen}
          >
            <span className={`qp-caret ${flashcardListOpen ? "is-open" : ""}`} aria-hidden="true">▸</span>
            <span className="qp-list-name">Forged Flashcards</span>
            <span className="qp-list-count">{flashcardList.length}</span>
          </button>
          {flashcardListOpen && (
            <ul className="qp-list">
              {flashcardList.length === 0 ? (
                <li className="qp-list-empty">{viewer ? "No flashcards yet." : "Log in to save flashcards."}</li>
              ) : (
                flashcardList.map((entry) => (
                  <li key={entry.id}>
                    <button
                      type="button"
                      className={`qp-list-item ${activeFlashcardHistoryId === entry.id ? "is-active" : ""}`}
                      onClick={() => onSelectFlashcard(entry)}
                      title={entry.title}
                    >
                      <span className="qp-list-item-emoji" aria-hidden="true">🃏</span>
                      <span className="qp-list-item-title">{entry.title}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      </div>

      <div className="qp-sidebar-bottom">
        {viewer ? (
          <div className="qp-user-card" title={`${viewerLabel} — ${viewerEmail}`}>
            <div className="qp-user-credits">
              <span className="qp-user-credits-value">
                {creditsBusy ? "..." : formatCreditCount(creditsRemaining)}
              </span>
              <span className="qp-user-credits-of"> / {formatCreditCount(AI_MONTHLY_CREDITS)} credits</span>
            </div>
            <div className="qp-user-main">
              <span className="qp-user-avatar" aria-hidden="true">{viewerInitialsLabel}</span>
              <span className="qp-user-id">
                <strong className="qp-user-name">{viewerLabel}</strong>
                <span className="qp-user-email">{viewerEmail}</span>
              </span>
            </div>
          </div>
        ) : (
          <button type="button" className="qp-login-btn" onClick={onLogin} data-tooltip="Log in">
            <span className="qp-login-avatar" aria-hidden="true">👤</span>
            <span className="qp-nav-label">Log in</span>
          </button>
        )}
      </div>
    </aside>
  );
}

export default function QuizPopz() {
  const [authReady, setAuthReady] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [authMode, setAuthMode] = useState("login");
  const [authName, setAuthName] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authRole, setAuthRole] = useState(DEFAULT_ROLE);
  const [selectedRole, setSelectedRole] = useState("");
  const [activeGenerator, setActiveGenerator] = useState("quiz");
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [topic, setTopic] = useState("Solar system science for grade 6");
  const [questionCount, setQuestionCount] = useState(10);
  const [provider, setProvider] = useState("openai");
  const [difficulty, setDifficulty] = useState("Intermediate");
  const [audience, setAudience] = useState("Middle school students");
  const [styleNotes, setStyleNotes] = useState("Make distractors clever but fair. Keep explanations short and high-value.");
  const [flashcardMode, setFlashcardMode] = useState("ai");
  const [flashcardTopic, setFlashcardTopic] = useState("Solar system science for grade 6");
  const [flashcardCount, setFlashcardCount] = useState(8);
  const [flashcardAudience, setFlashcardAudience] = useState("Middle school students");
  const [flashcardNotes, setFlashcardNotes] = useState("Make the deck memorable, clean, and easy to revise from.");
  const [manualFlashcards, setManualFlashcards] = useState(() => syncFlashcardsToCount([], DEFAULT_MANUAL_FLASHCARD_COUNT));
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingFlashcards, setIsGeneratingFlashcards] = useState(false);
  const [error, setError] = useState("");
  const [flashcardError, setFlashcardError] = useState("");
  const [resultMeta, setResultMeta] = useState(null);
  const [quizDraft, setQuizDraft] = useState(null);
  const [quizHistory, setQuizHistory] = useState([]);
  const [activeQuizHistoryId, setActiveQuizHistoryId] = useState("");
  const [activeQuizQuestionIndex, setActiveQuizQuestionIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizComplete, setQuizComplete] = useState(false);
  const [editorMode, setEditorMode] = useState(false);
  const [flashcardMeta, setFlashcardMeta] = useState(null);
  const [flashcardDeck, setFlashcardDeck] = useState(null);
  const [flashcardEditMode, setFlashcardEditMode] = useState(false);
  const [activeFlashcardIndex, setActiveFlashcardIndex] = useState(0);
  const [flippedFlashcards, setFlippedFlashcards] = useState({});
  const [flashcardHistory, setFlashcardHistory] = useState([]);
  const [activeFlashcardHistoryId, setActiveFlashcardHistoryId] = useState("");
  const [quizGenerateSeconds, setQuizGenerateSeconds] = useState(0);
  const [aiCredits, setAiCredits] = useState(() => createDefaultAiCredits());
  const [creditsBusy, setCreditsBusy] = useState(false);
  const [creditsError, setCreditsError] = useState("");
  const [actionToast, setActionToast] = useState("");
  const [theme, setTheme] = useState(() => readStoredTheme());
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [quizListOpen, setQuizListOpen] = useState(true);
  const [flashcardListOpen, setFlashcardListOpen] = useState(true);

  const providerMeta = useMemo(
    () => QUIZPOPZ_PROVIDERS.find((entry) => entry.id === provider) || QUIZPOPZ_PROVIDERS[0],
    [provider]
  );
  const effectiveRole = selectedRole && ROLE_CONFIG[selectedRole] ? selectedRole : DEFAULT_ROLE;
  const roleMeta = ROLE_CONFIG[effectiveRole];
  const viewerLabel = useMemo(() => {
    if (!viewer) return "";
    if (viewer.displayName) return viewer.displayName;
    if (viewer.email) return viewer.email.split("@")[0];
    return "QuizPopz user";
  }, [viewer]);
  const viewerEmail = viewer?.email || "";
  const viewerInitialsLabel = useMemo(() => viewerInitials(viewer, viewerLabel), [viewer, viewerLabel]);
  const sidebarQuery = sidebarSearch.trim().toLowerCase();
  const filteredQuizHistory = useMemo(
    () => (sidebarQuery ? quizHistory.filter((entry) => entry.title.toLowerCase().includes(sidebarQuery)) : quizHistory),
    [quizHistory, sidebarQuery]
  );
  const filteredFlashcardHistory = useMemo(
    () => (sidebarQuery ? flashcardHistory.filter((entry) => entry.title.toLowerCase().includes(sidebarQuery)) : flashcardHistory),
    [flashcardHistory, sidebarQuery]
  );
  const quizText = useMemo(() => formatQuizText(quizDraft), [quizDraft]);
  const answerKeyText = useMemo(() => formatAnswerKeyText(quizDraft), [quizDraft]);
  const quizScore = useMemo(() => scoreQuizDraft(quizDraft, quizAnswers), [quizDraft, quizAnswers]);
  const flashcardText = useMemo(() => formatFlashcardDeckText(flashcardDeck), [flashcardDeck]);
  const quizCreditsAvailable = hasAiCreditsFor(aiCredits, QUIZ_AI_CREDIT_COST);
  const flashcardCreditsAvailable = hasAiCreditsFor(aiCredits, FLASHCARD_AI_CREDIT_COST);
  const creditResetDate = formatCreditResetDate(aiCredits?.cycleMonth);

  function saveQuizHistoryPatch(entryId, patch) {
    if (!viewer?.uid || !entryId) return;
    setQuizHistory((prev) => {
      const next = prev.map((entry) => (
        entry.id === entryId
          ? normalizeQuizHistoryEntry({ ...entry, ...patch, updatedAt: Date.now() })
          : entry
      )).filter(Boolean).slice(0, QUIZ_HISTORY_LIMIT);
      writeQuizHistory(viewer.uid, next);
      return next;
    });
  }

  useEffect(() => {
    if (!viewer?.uid) {
      setAiCredits(createDefaultAiCredits());
      setCreditsBusy(false);
      setCreditsError("");
      return undefined;
    }

    setCreditsBusy(true);
    return onSnapshot(
      quizpopzCreditsDoc(viewer.uid),
      (snapshot) => {
        setAiCredits(normalizeAiCreditsRecord(snapshot.exists() ? snapshot.data() : null));
        setCreditsBusy(false);
        setCreditsError("");
      },
      (err) => {
        setAiCredits(createDefaultAiCredits());
        setCreditsBusy(false);
        setCreditsError(err.message || "Could not sync AI credits.");
      }
    );
  }, [viewer?.uid]);

  useEffect(() => {
    if (!viewer?.uid) {
      setQuizHistory([]);
      setActiveQuizHistoryId("");
      return undefined;
    }
    return onSnapshot(
      quizpopzQuizzesDoc(viewer.uid),
      (snapshot) => {
        setQuizHistory(normalizeQuizHistoryList(snapshot.exists() ? snapshot.data().entries : []));
      },
      () => {}
    );
  }, [viewer?.uid]);

  useEffect(() => {
    if (!viewer?.uid) {
      setFlashcardHistory([]);
      setActiveFlashcardHistoryId("");
      return undefined;
    }
    return onSnapshot(
      quizpopzFlashcardsDoc(viewer.uid),
      (snapshot) => {
        setFlashcardHistory(normalizeFlashcardHistoryList(snapshot.exists() ? snapshot.data().entries : []));
      },
      () => {}
    );
  }, [viewer?.uid]);

  useEffect(() => {
    let cancelled = false;
    if (!viewer?.uid) return undefined;
    getQuizPopzCredits()
      .then((data) => {
        if (!cancelled && data?.credits) setAiCredits(normalizeAiCreditsRecord(data.credits));
      })
      .catch(() => null);
    return () => {
      cancelled = true;
    };
  }, [viewer?.uid]);

  useEffect(() => {
    return onAuthStateChanged(auth, (nextUser) => {
      setViewer(nextUser);
      setAuthReady(true);
      setAuthBusy(false);
      setAuthPassword("");
      setAuthError("");
      setShowAuthModal(false);
      if (!nextUser) {
        setSelectedRole(DEFAULT_ROLE);
        setQuizHistory([]);
        setActiveQuizHistoryId("");
        setFlashcardHistory([]);
        setActiveFlashcardHistoryId("");
        return;
      }
      const storedRole = readStoredRole(nextUser.uid);
      if (!storedRole) {
        setSelectedRole(DEFAULT_ROLE);
        return;
      }
      const preset = ROLE_CONFIG[storedRole];
      const nextFlashcardCount = clampAiFlashcardCount(preset.presets.questionCount);
      setSelectedRole(storedRole);
      setTopic(preset.presets.topic);
      setQuestionCount(preset.presets.questionCount);
      setDifficulty(preset.presets.difficulty);
      setAudience(preset.presets.audience);
      setStyleNotes(preset.presets.styleNotes);
      setFlashcardTopic(preset.presets.topic);
      setFlashcardCount(nextFlashcardCount);
      setFlashcardAudience(preset.presets.audience);
      setFlashcardNotes(preset.presets.styleNotes);
      setManualFlashcards(syncFlashcardsToCount([], DEFAULT_MANUAL_FLASHCARD_COUNT));
    });
  }, []);

  useEffect(() => {
    writeStoredTheme(theme);
  }, [theme]);

  useEffect(() => {
    const cardCount = flashcardDeck?.cards?.length || 0;
    setActiveFlashcardIndex((prev) => {
      if (cardCount === 0) return 0;
      return Math.min(prev, cardCount - 1);
    });
  }, [flashcardDeck]);

  useEffect(() => {
    const questions = Array.isArray(quizDraft?.questions) ? quizDraft.questions : [];
    setActiveQuizQuestionIndex((prev) => {
      if (!questions.length) return 0;
      return Math.min(prev, questions.length - 1);
    });
    setQuizAnswers((prev) => {
      if (!questions.length) return {};
      const validKeys = new Set(questions.map((question, index) => quizQuestionKey(question, index)));
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (validKeys.has(key)) {
          next[key] = value;
        } else {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    if (!questions.length) setQuizComplete(false);
  }, [quizDraft]);

  useEffect(() => {
    if (!isGenerating) return undefined;
    const startedAt = Date.now();
    setQuizGenerateSeconds(0);
    const intervalId = window.setInterval(() => {
      setQuizGenerateSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 250);
    return () => window.clearInterval(intervalId);
  }, [isGenerating]);

  useEffect(() => {
    if (!actionToast) return undefined;
    const id = setTimeout(() => setActionToast(""), 2200);
    return () => clearTimeout(id);
  }, [actionToast]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setShowAuthModal(false);
        setSettingsOpen(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (!settingsOpen || typeof document === "undefined") return undefined;
    function handlePointerDown(event) {
      if (!event.target.closest || !event.target.closest(".qp-nav-settings-wrap")) {
        setSettingsOpen(false);
      }
    }
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [settingsOpen]);

  function resetQuizPlayer() {
    setActiveQuizQuestionIndex(0);
    setQuizAnswers({});
    setQuizComplete(false);
  }

  function applyRole(roleId, announce = true) {
    if (!ROLE_CONFIG[roleId]) return;
    const preset = ROLE_CONFIG[roleId].presets;
    setSelectedRole(roleId);
    setTopic(preset.topic);
    setQuestionCount(preset.questionCount);
    setDifficulty(preset.difficulty);
    setAudience(preset.audience);
    setStyleNotes(preset.styleNotes);
    setFlashcardTopic(preset.topic);
    setFlashcardCount(clampAiFlashcardCount(preset.questionCount));
    setFlashcardAudience(preset.audience);
    setFlashcardNotes(preset.styleNotes);
    setManualFlashcards(syncFlashcardsToCount([], DEFAULT_MANUAL_FLASHCARD_COUNT));
    setError("");
    setFlashcardError("");
    setResultMeta(null);
    setQuizDraft(null);
    setActiveQuizHistoryId("");
    resetQuizPlayer();
    setEditorMode(false);
    setFlashcardMeta(null);
    setFlashcardDeck(null);
    setFlashcardEditMode(false);
    setFlippedFlashcards({});
    setActiveFlashcardIndex(0);
    if (viewer && viewer.uid) writeStoredRole(viewer.uid, roleId);
    if (announce) setActionToast(`${ROLE_CONFIG[roleId].label} mode ready.`);
  }

  async function handleAuthSubmit(event) {
    event.preventDefault();
    setAuthBusy(true);
    setAuthError("");
    try {
      const normalizedEmail = authEmail.trim().toLowerCase();
      if (!normalizedEmail) throw new Error("Enter your email.");
      if (authMode === "login") {
        await signInWithEmailAndPassword(auth, normalizedEmail, authPassword);
      } else {
        const cred = await createUserWithEmailAndPassword(auth, normalizedEmail, authPassword);
        const displayName = authName.trim() || normalizedEmail.split("@")[0];
        await updateProfile(cred.user, { displayName: displayName.slice(0, 80) });
        writeStoredRole(cred.user.uid, authRole);
        applyRole(authRole, false);
      }
      setAuthPassword("");
    } catch (err) {
      setAuthError(prettyAuthError(err));
      setAuthBusy(false);
    }
  }

  async function handleSignOut() {
    const uid = viewer && viewer.uid ? viewer.uid : "";
    if (uid) clearStoredRole(uid);
    await signOut(auth);
    setQuizDraft(null);
    resetQuizPlayer();
    setResultMeta(null);
    setQuizHistory([]);
    setActiveQuizHistoryId("");
    setFlashcardHistory([]);
    setActiveFlashcardHistoryId("");
    setSelectedRole(DEFAULT_ROLE);
    setEditorMode(false);
    setFlashcardMeta(null);
    setFlashcardDeck(null);
    setFlashcardEditMode(false);
    setFlippedFlashcards({});
    setActiveFlashcardIndex(0);
    setAiCredits(createDefaultAiCredits());
    setCreditsBusy(false);
    setCreditsError("");
    setSettingsOpen(false);
    setWorkspaceOpen(false);
    setActionToast("Signed out.");
  }

  function requireLogin(message) {
    if (viewer) return true;
    setAuthMode("login");
    setAuthError("");
    setShowAuthModal(true);
    if (message) setActionToast(message);
    return false;
  }

  function handleOpenLogin() {
    setSettingsOpen(false);
    setAuthMode("login");
    setAuthError("");
    setShowAuthModal(true);
  }

  function handleNewQuiz() {
    setWorkspaceOpen(true);
    setActiveGenerator("quiz");
    setQuizDraft(null);
    setResultMeta(null);
    setActiveQuizHistoryId("");
    resetQuizPlayer();
    setEditorMode(false);
    setError("");
    setSettingsOpen(false);
    if (typeof document !== "undefined") {
      requestAnimationFrame(() => {
        const node = document.getElementById("quizpopz-results");
        if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    setActionToast("New quiz workspace ready.");
  }

  function handleNewFlashcardDeck() {
    setWorkspaceOpen(true);
    setActiveGenerator("flashcards");
    setFlashcardDeck(null);
    setFlashcardMeta(null);
    setActiveFlashcardHistoryId("");
    setFlashcardEditMode(false);
    setFlippedFlashcards({});
    setActiveFlashcardIndex(0);
    setFlashcardError("");
    setSettingsOpen(false);
    if (typeof document !== "undefined") {
      requestAnimationFrame(() => {
        const node = document.getElementById("quizpopz-flashcards");
        if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
    setActionToast("New flashcard deck workspace ready.");
  }

  function handleLoadFlashcardHistory(entry) {
    const normalizedEntry = normalizeFlashcardHistoryEntry(entry);
    if (!normalizedEntry) return;
    setWorkspaceOpen(true);
    setActiveGenerator("flashcards");
    setFlashcardDeck(cloneFlashcardDeck(normalizedEntry.deck));
    setFlashcardMeta(normalizedEntry.meta || null);
    setActiveFlashcardHistoryId(normalizedEntry.id);
    setFlashcardEditMode(false);
    setFlippedFlashcards({});
    setActiveFlashcardIndex(0);
    setActionToast("Flashcard deck loaded.");
    if (typeof document !== "undefined") {
      requestAnimationFrame(() => {
        const node = document.getElementById("quizpopz-flashcards");
        if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }

  function handleOpenHistory() {
    setSidebarOpen(true);
    setQuizListOpen(true);
    setFlashcardListOpen(true);
    setSettingsOpen(false);
    if (!viewer) {
      setActionToast("Log in to keep a history of your quizzes and decks.");
    }
  }

  function handleSidebarSearch() {
    setSidebarOpen(true);
    setQuizListOpen(true);
    setFlashcardListOpen(true);
    setSettingsOpen(false);
    if (typeof document !== "undefined") {
      requestAnimationFrame(() => {
        const node = document.getElementById("quizpopz-sidebar-search");
        if (node) node.focus();
      });
    }
  }

  function toggleTheme(nextTheme) {
    setTheme((prev) => {
      if (nextTheme === "light" || nextTheme === "dark") return nextTheme;
      return prev === "dark" ? "light" : "dark";
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!topic.trim()) {
      setError("Enter a topic so QuizPopz knows what to generate.");
      return;
    }
    if (!viewer) {
      requireLogin("Log in or sign up to generate a quiz.");
      return;
    }
    if (CLOSED_PROVIDER_IDS.has(provider)) {
      setError("That AI provider is closed right now. Use GPT 5.5 for quizzes.");
      setProvider("openai");
      return;
    }
    if (!quizCreditsAvailable) {
      setError(`Not enough AI credits. Wait till 1 month: ${creditResetDate}.`);
      return;
    }
    setQuizGenerateSeconds(0);
    setIsGenerating(true);
    setError("");

    try {
      const data = await generateQuizPopzQuiz({
        topic,
        questionCount,
        provider,
        difficulty,
        audience,
        styleNotes,
        role: selectedRole,
      });
      if (data.credits) setAiCredits(normalizeAiCreditsRecord(data.credits));
      setActiveGenerator("quiz");
      setResultMeta(data.meta || null);
      const nextQuiz = cloneQuizDraft(data.quiz);
      setQuizDraft(nextQuiz);
      if (viewer?.uid && nextQuiz) {
        const historyEntry = createQuizHistoryEntry(nextQuiz, data.meta || null);
        const nextHistory = [historyEntry, ...quizHistory].slice(0, QUIZ_HISTORY_LIMIT);
        setActiveQuizHistoryId(historyEntry.id);
        setQuizHistory(nextHistory);
        writeQuizHistory(viewer.uid, nextHistory);
      } else {
        setActiveQuizHistoryId("");
      }
      resetQuizPlayer();
      setEditorMode(false);
      setActionToast("Quiz generated.");
      if (typeof document !== "undefined") {
        requestAnimationFrame(() => {
          const resultsNode = document.getElementById("quizpopz-results");
          if (resultsNode) resultsNode.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    } catch (err) {
      if (err && err.details && err.details.credits) {
        setAiCredits(normalizeAiCreditsRecord(err.details.credits));
      }
      setError(err.message || "Quiz generation failed.");
    } finally {
      setIsGenerating(false);
    }
  }

  function handleQuickStart(scenario) {
    setTopic(scenario.topic);
    setAudience(scenario.audience);
    setDifficulty(scenario.difficulty);
    setQuestionCount(scenario.questionCount);
    setStyleNotes(scenario.styleNotes);
    setFlashcardTopic(scenario.topic);
    setFlashcardAudience(scenario.audience);
    setFlashcardNotes(scenario.styleNotes);
    setFlashcardCount(clampAiFlashcardCount(scenario.questionCount));
    setActionToast(`${scenario.label} preset loaded.`);
  }

  function handleLoadQuizHistory(entry) {
    if (!entry || !entry.quiz) return;
    const normalizedEntry = normalizeQuizHistoryEntry(entry);
    if (!normalizedEntry) return;
    setWorkspaceOpen(true);
    setActiveGenerator("quiz");
    setQuizDraft(cloneQuizDraft(normalizedEntry.quiz));
    setResultMeta(normalizedEntry.meta || null);
    setActiveQuizHistoryId(normalizedEntry.id);
    setActiveQuizQuestionIndex(normalizedEntry.activeQuestionIndex || 0);
    setQuizAnswers(sanitizeQuizAnswers(normalizedEntry.answers));
    setQuizComplete(Boolean(normalizedEntry.quizComplete));
    setEditorMode(false);
    setActionToast(normalizedEntry.quizComplete ? "Quiz and your answers loaded." : "Quiz loaded from history.");
    if (typeof document !== "undefined") {
      requestAnimationFrame(() => {
        const resultsNode = document.getElementById("quizpopz-results");
        if (resultsNode) resultsNode.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }

  function updateQuizField(field, value) {
    setQuizDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  function updateQuestion(index, updater) {
    setQuizDraft((prev) => {
      if (!prev) return prev;
      const questions = prev.questions.map((question, questionIndex) =>
        questionIndex === index ? updater(question) : question
      );
      return { ...prev, questions };
    });
  }

  function handleQuestionFieldChange(index, field, value) {
    updateQuestion(index, (question) => ({ ...question, [field]: value }));
  }

  function handleChoiceChange(questionIndex, choiceIndex, value) {
    updateQuestion(questionIndex, (question) => ({
      ...question,
      choices: question.choices.map((choice, currentChoiceIndex) =>
        currentChoiceIndex === choiceIndex ? { ...choice, text: value } : choice
      ),
    }));
  }

  function handleCorrectChoiceChange(questionIndex, choiceId) {
    updateQuestion(questionIndex, (question) => ({ ...question, correctChoiceId: choiceId }));
  }

  function handleQuizAnswer(questionIndex, choiceId) {
    if (quizComplete) return;
    const question = quizDraft?.questions?.[questionIndex];
    if (!question) return;
    const nextAnswers = {
      ...quizAnswers,
      [quizQuestionKey(question, questionIndex)]: choiceId,
    };
    setQuizAnswers(nextAnswers);
    saveQuizHistoryPatch(activeQuizHistoryId, {
      answers: nextAnswers,
      activeQuestionIndex: questionIndex,
      quizComplete: false,
      completedAt: 0,
    });
  }

  function handleQuizPrevious() {
    setActiveQuizQuestionIndex((prev) => {
      const nextIndex = Math.max(0, prev - 1);
      saveQuizHistoryPatch(activeQuizHistoryId, { activeQuestionIndex: nextIndex });
      return nextIndex;
    });
  }

  function handleQuizNext() {
    const questionTotal = quizDraft?.questions?.length || 0;
    setActiveQuizQuestionIndex((prev) => {
      const nextIndex = Math.min(questionTotal - 1, prev + 1);
      saveQuizHistoryPatch(activeQuizHistoryId, { activeQuestionIndex: nextIndex });
      return nextIndex;
    });
  }

  function handleQuizFinish() {
    setQuizComplete(true);
    saveQuizHistoryPatch(activeQuizHistoryId, {
      answers: quizAnswers,
      activeQuestionIndex: activeQuizQuestionIndex,
      quizComplete: true,
      completedAt: Date.now(),
    });
    setActionToast("Quiz scored.");
  }

  function handleQuizRetake() {
    resetQuizPlayer();
    saveQuizHistoryPatch(activeQuizHistoryId, {
      answers: {},
      activeQuestionIndex: 0,
      quizComplete: false,
      completedAt: 0,
    });
    setActionToast("Quiz restarted.");
  }

  function handleQuizJump(index) {
    setActiveQuizQuestionIndex(index);
    saveQuizHistoryPatch(activeQuizHistoryId, { activeQuestionIndex: index });
  }

  function handleManualFlashcardFieldChange(index, field, value) {
    setManualFlashcards((prev) => prev.map((card, cardIndex) => (
      cardIndex === index ? { ...card, [field]: value } : card
    )));
  }

  function handleAddManualFlashcard() {
    setManualFlashcards((prev) => [...prev, createEmptyFlashcard(prev.length)]);
  }

  function handleRemoveManualFlashcard(index) {
    setManualFlashcards((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, cardIndex) => cardIndex !== index);
    });
  }

  function handleAddDeckFlashcard() {
    if (!flashcardDeck) return;
    const nextCard = createEmptyFlashcard(flashcardDeck.cards.length);
    setFlashcardDeck({
      ...flashcardDeck,
      cards: [...flashcardDeck.cards, nextCard],
    });
    setActiveFlashcardIndex(flashcardDeck.cards.length);
  }

  function handleRemoveDeckFlashcard(index) {
    if (!flashcardDeck || flashcardDeck.cards.length <= 1) return;
    const removedCard = flashcardDeck.cards[index];
    const nextCards = flashcardDeck.cards.filter((_, cardIndex) => cardIndex !== index);

    setFlashcardDeck({
      ...flashcardDeck,
      cards: nextCards,
    });
    setActiveFlashcardIndex((prev) => Math.min(prev, nextCards.length - 1));
    setFlippedFlashcards((prev) => {
      if (!removedCard?.id || !prev[removedCard.id]) return prev;
      const next = { ...prev };
      delete next[removedCard.id];
      return next;
    });
  }

  function updateFlashcardDeckField(field, value) {
    setFlashcardDeck((prev) => (prev ? { ...prev, [field]: value } : prev));
  }

  function updateFlashcardDeckCard(index, field, value) {
    setFlashcardDeck((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        cards: prev.cards.map((card, cardIndex) => (
          cardIndex === index ? { ...card, [field]: value } : card
        )),
      };
    });
  }

  async function handleGenerateFlashcards(event) {
    event.preventDefault();
    if (!viewer) {
      requireLogin("Log in or sign up to forge flashcards.");
      return;
    }
    setFlashcardError("");
    setIsGeneratingFlashcards(true);

    try {
      let producedDeck = null;
      let producedMeta = null;
      if (flashcardMode === "manual") {
        const cleanedCards = manualFlashcards.map((card) => ({
          ...card,
          front: String(card.front || "").trim(),
          back: String(card.back || "").trim(),
          hint: String(card.hint || "").trim(),
        }));
        const hasPartialCards = cleanedCards.some((card) => (card.front || card.back || card.hint) && (!card.front || !card.back));
        const completedCards = cleanedCards.filter((card) => card.front && card.back);

        if (!completedCards.length) {
          throw new Error("Add at least one flashcard with both a front and a back.");
        }
        if (hasPartialCards) {
          throw new Error("Every manual flashcard needs both a front and a back. Finish or remove any partial cards.");
        }
        const deck = buildManualFlashcardDeck({
          topic: flashcardTopic,
          audience: flashcardAudience,
          role: selectedRole,
          cards: completedCards,
        });
        producedMeta = {
          providerLabel: "Manual deck",
          flashcardCount: deck.cards.length,
          mode: "manual",
        };
        producedDeck = cloneFlashcardDeck(deck);
        setFlashcardMeta(producedMeta);
        setFlashcardDeck(producedDeck);
      } else {
        if (!flashcardTopic.trim()) {
          throw new Error("Enter a topic so QuizPopz can generate flashcards.");
        }
        if (CLOSED_PROVIDER_IDS.has(provider)) {
          setProvider("openai");
          throw new Error("That AI provider is closed right now. Use GPT 5.5 for flashcards.");
        }
        if (!flashcardCreditsAvailable) {
          throw new Error(`Not enough AI credits. Wait till 1 month: ${creditResetDate}.`);
        }
        const data = await generateQuizPopzFlashcards({
          topic: flashcardTopic,
          flashcardCount,
          provider,
          audience: flashcardAudience,
          styleNotes: flashcardNotes,
          role: selectedRole,
        });
        if (data.credits) setAiCredits(normalizeAiCreditsRecord(data.credits));
        setActiveGenerator("flashcards");
        producedMeta = data.meta || null;
        producedDeck = cloneFlashcardDeck(data.deck);
        setFlashcardMeta(producedMeta);
        setFlashcardDeck(producedDeck);
      }

      if (viewer?.uid && producedDeck) {
        const historyEntry = createFlashcardHistoryEntry(producedDeck, producedMeta);
        const nextHistory = [historyEntry, ...flashcardHistory].slice(0, FLASHCARD_HISTORY_LIMIT);
        setActiveFlashcardHistoryId(historyEntry.id);
        setFlashcardHistory(nextHistory);
        writeFlashcardHistory(viewer.uid, nextHistory);
      } else {
        setActiveFlashcardHistoryId("");
      }

      setFlashcardEditMode(false);
      setActiveFlashcardIndex(0);
      setFlippedFlashcards({});
      setActionToast("Flashcards generated.");
      if (typeof document !== "undefined") {
        requestAnimationFrame(() => {
          const node = document.getElementById("quizpopz-flashcards");
          if (node) node.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }
    } catch (err) {
      if (err && err.details && err.details.credits) {
        setAiCredits(normalizeAiCreditsRecord(err.details.credits));
      }
      setFlashcardError(err.message || "Flashcard generation failed.");
    } finally {
      setIsGeneratingFlashcards(false);
    }
  }

  async function handleCopyQuiz() {
    if (!quizDraft) return;
    try {
      await copyText(quizText);
      setActionToast("Quiz copied.");
    } catch (_) {
      setActionToast("Could not copy quiz.");
    }
  }

  async function handleCopyAnswerKey() {
    if (!quizDraft) return;
    try {
      await copyText(answerKeyText);
      setActionToast("Answer key copied.");
    } catch (_) {
      setActionToast("Could not copy answer key.");
    }
  }

  async function handleCopyFlashcards() {
    if (!flashcardDeck) return;
    try {
      await copyText(flashcardText);
      setActionToast("Flashcards copied.");
    } catch (_) {
      setActionToast("Could not copy flashcards.");
    }
  }

  function handlePrintQuiz() {
    if (!quizDraft || typeof window === "undefined") return;
    const started = printHtmlDocument(buildQuizPrintDocument(quizDraft));
    if (!started) {
      setActionToast("Print dialog could not open. Check browser print permissions.");
    }
  }

  function handlePrintFlashcards() {
    if (!flashcardDeck || typeof window === "undefined") return;

    const cardsMarkup = flashcardDeck.cards.map((card, index) => `
      <article class="deck-card">
        <div class="deck-face">
          <span class="deck-kicker">Card ${index + 1} front</span>
          <h2>${escapeHtml(flashcardTextOrFallback(card && card.front, `Flashcard ${index + 1}`))}</h2>
        </div>
        <div class="deck-face deck-face-back">
          <span class="deck-kicker">Back</span>
          <p>${escapeHtml(flashcardTextOrFallback(card && card.back, "Add the back of this flashcard."))}</p>
          <small>${escapeHtml(normalizeFlashcardText(card && card.hint))}</small>
        </div>
      </article>
    `).join("");

    const printDocument = `<!doctype html>
      <html>
        <head>
          <title>${escapeHtml(flashcardTextOrFallback(flashcardDeck.title, "Flashcard Deck"))}</title>
          <style>
            body { font-family: "Trebuchet MS", Arial, sans-serif; margin: 32px; color: #16213e; }
            h1 { margin: 0 0 8px; }
            p { line-height: 1.6; }
            .deck-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px; margin-top: 24px; }
            .deck-card { border: 1px solid #d7dfe7; border-radius: 22px; overflow: hidden; break-inside: avoid; }
            .deck-face { padding: 18px; min-height: 140px; }
            .deck-face-back { background: #f5fbff; border-top: 1px solid #d7dfe7; }
            .deck-kicker { display: inline-block; margin-bottom: 12px; text-transform: uppercase; letter-spacing: 0.12em; font-size: 12px; color: #58708d; }
            h2 { margin: 0; font-size: 22px; }
            small { display: block; margin-top: 10px; color: #5d6e88; }
          </style>
        </head>
        <body>
          <h1>${escapeHtml(flashcardTextOrFallback(flashcardDeck.title, "Flashcard Deck"))}</h1>
          <p>${escapeHtml(normalizeFlashcardText(flashcardDeck.description))}</p>
          <p><strong>Study tip:</strong> ${escapeHtml(flashcardTextOrFallback(flashcardDeck.studyTip, "Flip through the deck twice: once for recognition, then again for recall."))}</p>
          <section class="deck-grid">${cardsMarkup}</section>
        </body>
      </html>`;

    const started = printHtmlDocument(printDocument);
    if (!started) {
      setActionToast("Print dialog could not open. Check browser print permissions.");
    }
  }

  function handleFlipFlashcard(index) {
    const card = flashcardDeck?.cards?.[index];
    if (!card) return;
    setFlippedFlashcards((prev) => ({
      ...prev,
      [card.id]: !prev[card.id],
    }));
  }

  function handleFlashcardPrev() {
    if (!flashcardDeck?.cards?.length) return;
    setActiveFlashcardIndex((prev) => (
      prev === 0 ? flashcardDeck.cards.length - 1 : prev - 1
    ));
  }

  function handleFlashcardNext() {
    if (!flashcardDeck?.cards?.length) return;
    setActiveFlashcardIndex((prev) => (
      prev === flashcardDeck.cards.length - 1 ? 0 : prev + 1
    ));
  }

  function handleFlashcardJump(index) {
    setActiveFlashcardIndex(index);
  }

  if (!authReady) {
    return (
      <div className={`qp-app qp-theme-${theme === "dark" ? "dark" : "light"}`}>
        <QuizPopzBoot />
      </div>
    );
  }

  return (
    <div
      className={`qp-app qp-theme-${theme === "dark" ? "dark" : "light"} ${sidebarOpen ? "qp-sidebar-open" : "qp-sidebar-collapsed"}`}
    >
      <QuizPopzSidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen((prev) => !prev)}
        theme={theme}
        onSetTheme={toggleTheme}
        viewer={viewer}
        viewerLabel={viewerLabel}
        viewerEmail={viewerEmail}
        viewerInitialsLabel={viewerInitialsLabel}
        creditsRemaining={aiCredits.remainingCredits}
        creditsBusy={creditsBusy}
        activeGenerator={activeGenerator}
        onNewQuiz={handleNewQuiz}
        onNewFlashcardDeck={handleNewFlashcardDeck}
        onHistory={handleOpenHistory}
        onSearch={handleSidebarSearch}
        settingsOpen={settingsOpen}
        onToggleSettings={() => setSettingsOpen((prev) => !prev)}
        onSignOut={handleSignOut}
        onLogin={handleOpenLogin}
        searchValue={sidebarSearch}
        onSearchChange={setSidebarSearch}
        quizList={filteredQuizHistory}
        flashcardList={filteredFlashcardHistory}
        activeQuizHistoryId={activeQuizHistoryId}
        activeFlashcardHistoryId={activeFlashcardHistoryId}
        onSelectQuiz={handleLoadQuizHistory}
        onSelectFlashcard={handleLoadFlashcardHistory}
        quizListOpen={quizListOpen}
        onToggleQuizList={() => setQuizListOpen((prev) => !prev)}
        flashcardListOpen={flashcardListOpen}
        onToggleFlashcardList={() => setFlashcardListOpen((prev) => !prev)}
      />
      <main className={`quizpopz-page quizpopz-app-main quizpopz-role-${effectiveRole}`}>
        {workspaceOpen ? (
          <>
        <section className="quizpopz-hero quizpopz-hero--compact">
          <div className="quizpopz-hero-copy">
            <div className="quizpopz-toolbar">
              <span className="quizpopz-welcome">
                {viewer ? `Welcome, ${viewerLabel}` : "Welcome to QuizPopz"}
              </span>
            </div>
            <span className="quizpopz-eyebrow">{roleMeta.eyebrow}</span>
            <h1>QuizPopz</h1>
            <p>{roleMeta.heroBody}</p>
            <div className="quizpopz-ribbon-row quizpopz-ribbon-row--animated">
              {roleMeta.motionWords.map((word) => (
                <span key={word}>{word}</span>
              ))}
            </div>
            <div className="quizpopz-hero-stats">
              <div>
                <strong>{roleMeta.label}</strong>
                <span>Active UI mode</span>
              </div>
              <div>
                <strong>{questionCount}</strong>
                <span>MCQs requested</span>
              </div>
              <div>
                <strong>{providerMeta.label}</strong>
                <span>Selected AI engine</span>
              </div>
              <div>
                <strong>{creditsBusy ? "..." : formatCreditCount(aiCredits.remainingCredits)}</strong>
                <span>AI credits left</span>
              </div>
            </div>
          </div>
          <div className="quizpopz-hero-panel">
            <div className="quizpopz-orb quizpopz-orb-a" />
            <div className="quizpopz-orb quizpopz-orb-b" />
            <div className="quizpopz-orb quizpopz-orb-c" />
            <div className="quizpopz-preview-card">
              <span className="quizpopz-preview-label">Live prompt target</span>
              <h2>{providerMeta.label}</h2>
              <p>{roleMeta.heroTitle}</p>
              <ul>
                <li>{roleMeta.promptNudge}</li>
                <li>JSON-only output contract</li>
                <li>Editable, printable, copyable output</li>
                <li>Answer key and explanations</li>
              </ul>
            </div>
          </div>
        </section>

        {activeGenerator === "quiz" && (
          <>
        <section className="quizpopz-workspace quizpopz-tool-workspace">
          <form className="quizpopz-builder-card" onSubmit={handleSubmit}>
            <div className="quizpopz-section-heading">
              <span>Builder</span>
              <h2>{roleMeta.heroTitle}</h2>
            </div>

            <label className="quizpopz-field">
              <span>{roleMeta.topicLabel}</span>
              <input
                type="text"
                value={topic}
                onChange={(event) => setTopic(event.target.value)}
                placeholder="Example: Fractions, photosynthesis, World War 2, coding basics"
              />
            </label>

            <div className="quizpopz-grid-two">
              <label className="quizpopz-field">
                <span>{roleMeta.audienceLabel}</span>
                <input
                  type="text"
                  value={audience}
                  onChange={(event) => setAudience(event.target.value)}
                  placeholder={roleMeta.audiencePlaceholder}
                />
              </label>
              <label className="quizpopz-field">
                <span>Difficulty</span>
                <select value={difficulty} onChange={(event) => setDifficulty(event.target.value)}>
                  {DIFFICULTY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="quizpopz-field">
              <span>How many MCQs?</span>
              <div className="quizpopz-chip-row">
                {QUESTION_COUNT_OPTIONS.map((option) => (
                  <button
                    key={option}
                    type="button"
                    className={`quizpopz-chip ${questionCount === option ? "is-selected" : ""}`}
                    onClick={() => setQuestionCount(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <div className="quizpopz-field">
              <span>Choose the AI</span>
              <div className="quizpopz-provider-grid">
                {QUIZPOPZ_PROVIDERS.map((entry) => {
                  const isClosed = CLOSED_PROVIDER_IDS.has(entry.id);
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      className={`quizpopz-provider-card accent-${entry.accent} ${provider === entry.id ? "is-selected" : ""} ${isClosed ? "is-closed" : ""}`}
                      onClick={() => {
                        if (!isClosed) setProvider(entry.id);
                      }}
                      disabled={isClosed}
                      aria-disabled={isClosed}
                    >
                      {isClosed && <span className="quizpopz-closed-tape">Closed</span>}
                      <strong>{entry.label}</strong>
                      <span>{entry.description}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="quizpopz-field">
              <span>{roleMeta.notesLabel}</span>
              <textarea
                rows="4"
                value={styleNotes}
                onChange={(event) => setStyleNotes(event.target.value)}
                placeholder={roleMeta.notesPlaceholder}
              />
            </label>

            {error && <div className="quizpopz-error-banner">{error}</div>}

            <button type="submit" className="quizpopz-generate-btn" disabled={isGenerating || creditsBusy || !quizCreditsAvailable}>
              {isGenerating
                ? `Generating quiz... ${formatElapsedTime(quizGenerateSeconds)}`
                : quizCreditsAvailable
                  ? `Generate ${roleMeta.label.toLowerCase()} quiz (${QUIZ_AI_CREDIT_COST} credits)`
                  : `Wait till 1 month: ${creditResetDate}`}
            </button>
            {isGenerating && (
              <div className="quizpopz-generation-timer" role="status" aria-live="polite">
                <strong>{formatElapsedTime(quizGenerateSeconds)}</strong>
                <span>Elapsed while QuizPopz builds your quiz</span>
              </div>
            )}
          </form>

          <QuizPopzAiCreditsPanel compact requiredCost={QUIZ_AI_CREDIT_COST} aiCredits={aiCredits} creditsBusy={creditsBusy} creditsError={creditsError} />

          <aside className="quizpopz-sidebar-card">
            <div className="quizpopz-section-heading">
              <span>{roleMeta.panelTitle}</span>
              <h2>Shape the experience</h2>
            </div>
            <ul className="quizpopz-feature-list">
              <li>{roleMeta.panelBody}</li>
              <li>Strict MCQ JSON schema with exactly four choices per question.</li>
              <li>Provider-specific model routing so you can swap engines without changing the UI.</li>
              <li>Safe localhost key flow later via env vars, not hardcoded client secrets.</li>
            </ul>
            <div className="quizpopz-quickstart-grid">
              {roleMeta.quickStarts.map((scenario, index) => (
                <button
                  key={scenario.label}
                  type="button"
                  className="quizpopz-quickstart-card"
                  style={{ "--quizpopz-stagger": `${index * 90}ms` }}
                  onClick={() => handleQuickStart(scenario)}
                >
                  <strong>{scenario.label}</strong>
                  <span>{scenario.caption}</span>
                </button>
              ))}
            </div>
            <div className="quizpopz-status-panel">
              <span>Selected provider</span>
              <strong>{providerMeta.label}</strong>
              <small>Model target: {providerMeta.modelName}</small>
            </div>
          </aside>
        </section>

        <section className="quizpopz-results-shell" id="quizpopz-results">
          {quizHistory.length > 0 && (
            <div className="quizpopz-history-bar" aria-label="Generated quiz history">
              {quizHistory.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  className={`quizpopz-history-item ${activeQuizHistoryId === entry.id ? "is-active" : ""}`}
                  onClick={() => handleLoadQuizHistory(entry)}
                  title={entry.quizComplete ? `${entry.title} - saved answers included` : entry.title}
                >
                  {entry.title}
                </button>
              ))}
            </div>
          )}
          <div className="quizpopz-results-heading">
            <div>
              <span>Output</span>
              <h2>Editable quiz draft</h2>
            </div>
            <div className="quizpopz-result-actions">
              {resultMeta && (
                <div className="quizpopz-meta-pill-row">
                  <span>{resultMeta.providerLabel}</span>
                  <span>{resultMeta.questionCount} questions</span>
                  <span>{resultMeta.mode}</span>
                </div>
              )}
              <button type="button" className="quizpopz-action-btn" onClick={() => setEditorMode((prev) => !prev)} disabled={!quizDraft}>
                {editorMode ? "Preview mode" : "Edit quiz"}
              </button>
              <button type="button" className="quizpopz-action-btn" onClick={handleCopyQuiz} disabled={!quizDraft}>
                Copy quiz
              </button>
              <button type="button" className="quizpopz-action-btn" onClick={handleCopyAnswerKey} disabled={!quizDraft}>
                Copy answer key
              </button>
              <button type="button" className="quizpopz-action-btn" onClick={handlePrintQuiz} disabled={!quizDraft}>
                Print
              </button>
            </div>
          </div>

          {!quizDraft ? (
            <div className="quizpopz-empty-state">
              <h3>No quiz generated yet</h3>
              <p>
                Use the builder to generate a quiz. This space becomes your editable draft with copy,
                answer-key, and print actions once QuizPopz returns the quiz.
              </p>
            </div>
          ) : (
            <div className="quizpopz-results-grid quizpopz-print-surface">
              <section className="quizpopz-results-main">
                <div className="quizpopz-summary-card">
                  {editorMode ? (
                    <div className="quizpopz-editor-stack">
                      <label className="quizpopz-inline-field">
                        <span>Quiz title</span>
                        <input
                          type="text"
                          value={quizDraft.title}
                          onChange={(event) => updateQuizField("title", event.target.value)}
                        />
                      </label>
                      <label className="quizpopz-inline-field">
                        <span>Description</span>
                        <input
                          type="text"
                          value={quizDraft.description}
                          onChange={(event) => updateQuizField("description", event.target.value)}
                        />
                      </label>
                      <label className="quizpopz-inline-field">
                        <span>Instructions</span>
                        <textarea
                          rows="3"
                          value={quizDraft.instructions}
                          onChange={(event) => updateQuizField("instructions", event.target.value)}
                        />
                      </label>
                    </div>
                  ) : (
                    <>
                      <span className="quizpopz-summary-kicker">{quizDraft.title}</span>
                      <h3>{quizDraft.description}</h3>
                      <p>{quizDraft.instructions}</p>
                    </>
                  )}
                </div>
                {editorMode ? (
                  <div className="quizpopz-question-grid">
                    {quizDraft.questions.map((question, index) => (
                      <QuizQuestionCard
                        key={question.id || `${question.prompt}-${index}`}
                        question={question}
                        index={index}
                        editable={editorMode}
                        onQuestionFieldChange={handleQuestionFieldChange}
                        onChoiceChange={handleChoiceChange}
                        onCorrectChoiceChange={handleCorrectChoiceChange}
                      />
                    ))}
                  </div>
                ) : (
                  <QuizPlayer
                    quiz={quizDraft}
                    activeIndex={activeQuizQuestionIndex}
                    answers={quizAnswers}
                    isComplete={quizComplete}
                    score={quizScore}
                    onAnswer={handleQuizAnswer}
                    onPrevious={handleQuizPrevious}
                    onNext={handleQuizNext}
                    onFinish={handleQuizFinish}
                    onRetake={handleQuizRetake}
                    onJump={handleQuizJump}
                  />
                )}
              </section>

              <aside className="quizpopz-results-side">
                <div className="quizpopz-answer-key-card">
                  {editorMode || quizComplete ? (
                    <>
                      <span>Answer key</span>
                      <ol className="quizpopz-answer-key-list">
                        {quizDraft.questions.map((question, index) => (
                          <li key={`${question.id || index}-answer`}>
                            <strong>{index + 1}. {question.correctChoiceId}</strong>
                            <p>{question.explanation}</p>
                          </li>
                        ))}
                      </ol>
                    </>
                  ) : (
                    <>
                      <span>Quiz progress</span>
                      <div className="quizpopz-score-grid quizpopz-score-grid--compact">
                        <div>
                          <strong>{activeQuizQuestionIndex + 1}</strong>
                          <span>Current</span>
                        </div>
                        <div>
                          <strong>{quizScore.answered}</strong>
                          <span>Answered</span>
                        </div>
                        <div>
                          <strong>{quizScore.total - quizScore.answered}</strong>
                          <span>Remaining</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </aside>
            </div>
          )}
        </section>
          </>
        )}

        {activeGenerator === "flashcards" && (
        <section className="quizpopz-results-shell quizpopz-flashcards-shell" id="quizpopz-flashcards">
          <div className="quizpopz-results-heading">
            <div>
              <span>Flashcard Maker 2000</span>
              <h2>Flashcard Maker 2000</h2>
            </div>
            <div className="quizpopz-result-actions">
              {flashcardMeta && (
                <div className="quizpopz-meta-pill-row">
                  <span>{flashcardMeta.providerLabel}</span>
                  <span>{flashcardMeta.flashcardCount} cards</span>
                  <span>{flashcardMeta.mode}</span>
                </div>
              )}
              <button
                type="button"
                className="quizpopz-action-btn"
                onClick={() => setFlashcardEditMode((prev) => !prev)}
                disabled={!flashcardDeck}
              >
                {flashcardEditMode ? "Preview deck" : "Edit deck"}
              </button>
              <button type="button" className="quizpopz-action-btn" onClick={handleCopyFlashcards} disabled={!flashcardDeck}>
                Copy deck
              </button>
              <button type="button" className="quizpopz-action-btn" onClick={handlePrintFlashcards} disabled={!flashcardDeck}>
                Print deck
              </button>
            </div>
          </div>

          <div className="quizpopz-flashcards-layout quizpopz-tool-workspace">
            <form className="quizpopz-builder-card quizpopz-flashcard-builder" onSubmit={handleGenerateFlashcards}>
              <div className="quizpopz-section-heading">
                <span>Flashcard flow</span>
                <h2>Build cards manually or let AI forge the deck</h2>
              </div>

              <div className="quizpopz-mode-row">
                <button
                  type="button"
                  className={`quizpopz-mode-pill ${flashcardMode === "ai" ? "is-active" : ""}`}
                  onClick={() => setFlashcardMode("ai")}
                >
                  AI topic mode
                </button>
                <button
                  type="button"
                  className={`quizpopz-mode-pill ${flashcardMode === "manual" ? "is-active" : ""}`}
                  onClick={() => setFlashcardMode("manual")}
                >
                  Manual card mode
                </button>
              </div>

              <label className="quizpopz-field">
                <span>{flashcardMode === "manual" ? "Deck topic or title" : "Flashcard topic"}</span>
                <input
                  type="text"
                  value={flashcardTopic}
                  onChange={(event) => setFlashcardTopic(event.target.value)}
                  placeholder="Example: Cell organelles, algebra formulas, history dates, vocabulary"
                />
              </label>

              <div className="quizpopz-grid-two">
                <label className="quizpopz-field">
                  <span>{roleMeta.audienceLabel}</span>
                  <input
                    type="text"
                    value={flashcardAudience}
                    onChange={(event) => setFlashcardAudience(event.target.value)}
                    placeholder={roleMeta.audiencePlaceholder}
                  />
                </label>
                {flashcardMode === "ai" ? (
                  <div className="quizpopz-field">
                    <span>How many flashcards? (max 25)</span>
                    <div className="quizpopz-chip-row">
                      {FLASHCARD_COUNT_OPTIONS.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`quizpopz-chip ${flashcardCount === option ? "is-selected" : ""}`}
                          onClick={() => setFlashcardCount(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="quizpopz-field">
                    <span>Manual deck size</span>
                    <div className="quizpopz-manual-editor-toolbar">
                      <p>{manualFlashcards.length} cards in your draft. Add as many as you want.</p>
                      <button type="button" className="quizpopz-action-btn" onClick={handleAddManualFlashcard}>
                        Add flashcard
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {flashcardMode === "ai" ? (
                <>
                  <div className="quizpopz-field">
                    <span>Choose the AI</span>
                    <div className="quizpopz-provider-grid">
                      {QUIZPOPZ_PROVIDERS.map((entry) => {
                        const isClosed = CLOSED_PROVIDER_IDS.has(entry.id);
                        return (
                          <button
                            key={entry.id}
                            type="button"
                            className={`quizpopz-provider-card accent-${entry.accent} ${provider === entry.id ? "is-selected" : ""} ${isClosed ? "is-closed" : ""}`}
                            onClick={() => {
                              if (!isClosed) setProvider(entry.id);
                            }}
                            disabled={isClosed}
                            aria-disabled={isClosed}
                          >
                            {isClosed && <span className="quizpopz-closed-tape">Closed</span>}
                            <strong>{entry.label}</strong>
                            <span>{entry.description}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <label className="quizpopz-field">
                    <span>{roleMeta.notesLabel}</span>
                    <textarea
                      rows="4"
                      value={flashcardNotes}
                      onChange={(event) => setFlashcardNotes(event.target.value)}
                      placeholder="Mention memory tricks, definition depth, examples, or what the deck should emphasize."
                    />
                  </label>
                </>
              ) : (
                <div className="quizpopz-manual-editor-grid">
                  {manualFlashcards.map((card, index) => (
                    <FlashcardEditorCard
                      key={card.id}
                      card={card}
                      index={index}
                      tone="Manual draft"
                      onFieldChange={handleManualFlashcardFieldChange}
                      onRemove={handleRemoveManualFlashcard}
                      canRemove={manualFlashcards.length > 1}
                    />
                  ))}
                </div>
              )}

              {flashcardError && <div className="quizpopz-error-banner">{flashcardError}</div>}

              <button type="submit" className="quizpopz-generate-btn" disabled={isGeneratingFlashcards || (flashcardMode === "ai" && (creditsBusy || !flashcardCreditsAvailable))}>
                {isGeneratingFlashcards
                  ? "Generating flashcards..."
                  : flashcardMode === "manual"
                    ? "Generate flashcards from manual cards"
                    : flashcardCreditsAvailable
                      ? `Generate flashcards with AI (${FLASHCARD_AI_CREDIT_COST} credits)`
                      : `Wait till 1 month: ${creditResetDate}`}
              </button>
            </form>

            <QuizPopzAiCreditsPanel compact requiredCost={FLASHCARD_AI_CREDIT_COST} aiCredits={aiCredits} creditsBusy={creditsBusy} creditsError={creditsError} />

            <aside className="quizpopz-sidebar-card quizpopz-flashcards-aside">
              <div className="quizpopz-section-heading">
                <span>Designer motion</span>
                <h2>Flashcard Maker 2000 notes</h2>
              </div>
              <ul className="quizpopz-feature-list">
                <li>Flip animation with layered card-stack motion and a jump rail.</li>
                <li>Use AI when you want topic coverage fast; use manual mode when you want total control.</li>
                <li>Decks stay editable after generation, so you can polish them before printing or sharing.</li>
                <li>Fonts, spacing, gradients, and motion are tuned to feel designed, not generic.</li>
              </ul>
              <div className="quizpopz-quickstart-grid">
                {roleMeta.quickStarts.map((scenario, index) => (
                  <button
                    key={`${scenario.label}-flashcards`}
                    type="button"
                    className="quizpopz-quickstart-card"
                    style={{ "--quizpopz-stagger": `${index * 90}ms` }}
                    onClick={() => handleQuickStart(scenario)}
                  >
                    <strong>{scenario.label}</strong>
                    <span>Load this topic into both the quiz and flashcard builders.</span>
                  </button>
                ))}
              </div>
              <div className="quizpopz-status-panel">
                <span>Active deck mode</span>
                <strong>{flashcardMode === "manual" ? "Manual flashcards" : providerMeta.label}</strong>
                <small>{flashcardMode === "manual" ? "You control every front and back." : `Model target: ${providerMeta.modelName}`}</small>
              </div>
            </aside>
          </div>

          {!flashcardDeck ? (
            <div className="quizpopz-empty-state">
              <h3>No flashcard deck yet</h3>
              <p>
                Choose AI mode to generate cards from a topic, or switch to manual mode and type each card yourself.
                Then generate the deck and flip through it below.
              </p>
            </div>
          ) : (
            <div className="quizpopz-flashcards-output">
              <section className="quizpopz-results-main">
                <div className="quizpopz-summary-card quizpopz-flashcard-summary-card">
                  {flashcardEditMode ? (
                    <div className="quizpopz-editor-stack">
                      <label className="quizpopz-inline-field">
                        <span>Deck title</span>
                        <input
                          type="text"
                          value={flashcardDeck.title}
                          onChange={(event) => updateFlashcardDeckField("title", event.target.value)}
                        />
                      </label>
                      <label className="quizpopz-inline-field">
                        <span>Description</span>
                        <input
                          type="text"
                          value={flashcardDeck.description}
                          onChange={(event) => updateFlashcardDeckField("description", event.target.value)}
                        />
                      </label>
                      <label className="quizpopz-inline-field">
                        <span>Study tip</span>
                        <textarea
                          rows="3"
                          value={flashcardDeck.studyTip}
                          onChange={(event) => updateFlashcardDeckField("studyTip", event.target.value)}
                        />
                      </label>
                    </div>
                  ) : (
                    <>
                      <span className="quizpopz-summary-kicker">{flashcardDeck.title}</span>
                      <h3>{flashcardDeck.description}</h3>
                      <p>{flashcardDeck.studyTip}</p>
                    </>
                  )}
                </div>

                <FlashcardStage
                  deck={flashcardDeck}
                  activeIndex={activeFlashcardIndex}
                  flippedCards={flippedFlashcards}
                  onFlip={handleFlipFlashcard}
                  onPrev={handleFlashcardPrev}
                  onNext={handleFlashcardNext}
                  onJump={handleFlashcardJump}
                />

                {flashcardEditMode && (
                  <>
                    <div className="quizpopz-manual-editor-toolbar">
                      <p>{flashcardDeck.cards.length} cards in this deck. Edit the wording, remove weak cards, or add new ones before copying or printing.</p>
                      <button type="button" className="quizpopz-action-btn" onClick={handleAddDeckFlashcard}>
                        Add flashcard
                      </button>
                    </div>

                    <div className="quizpopz-flashcard-editor-grid">
                      {flashcardDeck.cards.map((card, index) => (
                        <FlashcardEditorCard
                          key={card.id || `deck-card-${index}`}
                          card={card}
                          index={index}
                          tone="Editable deck"
                          onFieldChange={updateFlashcardDeckCard}
                          onRemove={handleRemoveDeckFlashcard}
                          canRemove={flashcardDeck.cards.length > 1}
                        />
                      ))}
                    </div>
                  </>
                )}
              </section>

              <aside className="quizpopz-results-side">
                <div className="quizpopz-answer-key-card quizpopz-flashcard-list-card">
                  <span>Deck list</span>
                  <ol className="quizpopz-answer-key-list">
                    {flashcardDeck.cards.map((card, index) => (
                      <li key={`${card.id || index}-flashcard-list`}>
                        <strong>{index + 1}. {card.front}</strong>
                        <p>{card.back}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              </aside>
            </div>
          )}
        </section>
        )}
          </>
        ) : (
          <section className="quizpopz-welcome-screen">
            <h1 className="quizpopz-welcome-big">
              {viewer ? `Welcome, ${viewerLabel}` : "Welcome to QuizPopz"}
            </h1>
            <p className="quizpopz-welcome-hint">
              Pick New Quiz or New Flashcard Deck from the sidebar to begin.
            </p>
          </section>
        )}
        {actionToast && <div className="quizpopz-toast">{actionToast}</div>}
      </main>

      {showAuthModal && (
        <QuizPopzAuthModal
          authMode={authMode}
          authName={authName}
          authEmail={authEmail}
          authPassword={authPassword}
          authBusy={authBusy}
          authError={authError}
          authRole={authRole}
          onModeChange={(nextMode) => {
            setAuthMode(nextMode);
            setAuthError("");
          }}
          onNameChange={setAuthName}
          onRoleChange={setAuthRole}
          onEmailChange={setAuthEmail}
          onPasswordChange={setAuthPassword}
          onSubmit={handleAuthSubmit}
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </div>
  );
}